import React, { useEffect, useState, useMemo } from 'react';
import splashImg from '../splash.png';
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import MasterLayout from "../masterLayout/MasterLayout";
import { API_URL, LOCATION_ID, TOKEN } from "..";
import sanitizeHtml from 'sanitize-html';
import { Parser } from 'htmlparser2';
import { Modal, Button, Form, Spinner, Offcanvas, Table } from 'react-bootstrap';
import { calculateTaxAmounts } from '../helpers/taxCalculator';
import { calculateItemPrice } from '../helpers/invoiceItemHelpers';
import { buildQuoteFileName } from '../helpers/documentFileName';
import {
  parseTermsConditions,
  parseUserSignature,
  formatQuoteSubjectDate,
  blobToBase64,
} from '../helpers/coppergatDefaults';
import {
  renderCoppergatSignaturePdf,
  renderCoppergatTermsPdf,
  renderCoppergatSignatureHtml,
  renderCoppergatTermsHtml,
} from '../helpers/coppergatTemplateParts';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import sapImg from '../sap.png';
import kemaImg from '../kema.png';
import ceImg from '../ce.jpg';
import pkstandImg from '../pkstand.png';
import psqcaImg from '../psqca.png';
import tuvImg from '../tuv.png';
import alonImg from '../alon.png';

const getEffectiveDiscountPercentage = (subtotal, discount, type) => {
  if (!subtotal || !discount) return 0;
  return type === 'fixed' ? (Number(discount) / subtotal) * 100 : Number(discount);
};

const getDiscountAmount = (subtotal, discount, type) => {
  if (!subtotal || !discount) return 0;
  return type === 'fixed' ? Number(discount) : (subtotal * Number(discount)) / 100;
};

// ===== Sample Data =====
const parseHtmlToPdf = (html) => {
  const elements = [];
  let currentText = '';
  let currentStyles = {};
  let isInsideList = false;
  let isInsideListItem = false;
  let listItemIndex = 0;

  const parser = new Parser(
    {
      onopentag(name, attribs) {
        if (name === 'p') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={styles.descriptionParagraph}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          currentStyles = { ...styles.descriptionParagraph };
        } else if (name === 'strong') {
          currentStyles = { ...currentStyles, ...styles.descriptionBold };
        } else if (name === 'br') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          elements.push(
            <Text key={elements.length} style={currentStyles}>
              {'\n'}
            </Text>
          );
        } else if (name === 'ul' || name === 'ol') {
          isInsideList = true;
          listItemIndex = 0;
        } else if (name === 'li') {
          isInsideListItem = true;
          listItemIndex += 1;
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          currentStyles = { ...styles.descriptionParagraph, ...styles.listItem };
          if (attribs['data-list'] === 'ordered') {
            currentText = `${listItemIndex}. `;
          } else if (attribs['data-list'] === 'bullet') {
            currentText = 'â€¢ ';
          } else {
            currentText = 'â€¢ ';
          }
        }
      },
      ontext(text) {
        currentText += text.trim();
      },
      onclosetag(name) {
        if (name === 'p' || name === 'strong' || name === 'li') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          if (name === 'strong') {
            currentStyles = { ...styles.descriptionParagraph };
          } else if (name === 'li') {
            isInsideListItem = false;
            currentStyles = { ...styles.descriptionParagraph };
          }
        } else if (name === 'ul' || name === 'ol') {
          isInsideList = false;
          listItemIndex = 0;
        }
      },
    },
    { decodeEntities: true }
  );

  parser.write(
    sanitizeHtml(html, {
      allowedTags: ['p', 'strong', 'br', 'ul', 'ol', 'li'],
      allowedAttributes: {
        li: ['data-list'],
      },
    })
  );
  parser.end();

  if (currentText) {
    elements.push(
      <Text key={elements.length} style={styles.descriptionParagraph}>
        {currentText}
      </Text>
    );
  }

  return elements.length > 0 ? (
    <View>{elements}</View>
  ) : (
    <Text style={styles.descriptionParagraph}>N/A</Text>
  );
};
// ===== Utility Functions =====
const calculateSubtotal = (items) =>
  items.reduce((sum, i) => sum + (i.price || 0), 0);
const calculateTax = (data) => {
  const { totalTax } = calculateTaxAmounts(calculateSubtotal(data?.items), getEffectiveDiscountPercentage(calculateSubtotal(data?.items), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type);
  return totalTax;
};
const calculateTotal = (data) => {
  if (!data) return 0;
  const { total } = calculateTaxAmounts(calculateSubtotal(data?.items), getEffectiveDiscountPercentage(calculateSubtotal(data?.items), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type);
  return total;
};

const convertDateFormat = (date) => new Date(date).toLocaleDateString();

// ====== React Preview Templates ======
const ClassicTemplate = ({ data, footerContent }) => (
  <div
    style={{
      fontSize: 11,
      border: "2px solid #2c3e50",
      padding: 20,
      backgroundColor: "#fff",
      fontFamily: "Arial, sans-serif",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 20,
        borderBottom: "3px solid #2c3e50",
        paddingBottom: 15,
      }}
    >
      <img
        src={API_URL + data.company.logo}
        alt="Logo"
        style={{ width: 180, height: 65, objectFit: 'contain' }}
      />
      <div style={{ textAlign: "right", fontSize: 9, color: "#2c3e50" }}>
        <div style={{ fontWeight: "bold", fontSize: 11 }}>
          {data.company.name}
        </div>
        <div>{data.company.address}</div>
        <div>VAT: {data.company.vat}</div>
        <div>{data.company.email}</div>
      </div>
    </div>
    <h6
      style={{
        textAlign: "center",
        color: "#2c3e50",
        backgroundColor: "#ecf0f1",
        padding: 10,
        margin: 0,
        marginBottom: 20,
      }}
    >
      QUOTE
    </h6>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 20,
      }}
    >
      <div style={{ fontSize: 10 }}>
        <div style={{ fontWeight: "bold", marginBottom: 5 }}>Bill To:</div>
        <div>{data.customer_name}</div>
        <div>{data.address}</div>
        <div>{data.phone}</div>
        {data.email && <div>{data.email}</div>}
      </div>
      <div style={{ textAlign: "right", fontSize: 10 }}>
        <div>
          QUOTE #:{" "}
          <span style={{ fontWeight: "bold" }}>{data.invoice_number}</span>
        </div>
        <div>Date: {convertDateFormat(data.issue_date)}</div>
        <div>Order #: {data.order_number}</div>
      </div>
    </div>
    <table
      style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}
    >
      <thead>
        <tr style={{ backgroundColor: "#2c3e50", color: "#fff" }}>
          <th
            style={{
              padding: 8,
              textAlign: "left",
              border: "1px solid #2c3e50",
            }}
          >
            Description
          </th>
          <th style={{ padding: 8, border: "1px solid #2c3e50" }}>Qty</th>
          <th style={{ padding: 8, border: "1px solid #2c3e50" }}>
            Unit Price
          </th>
          <th style={{ padding: 8, border: "1px solid #2c3e50" }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((i, idx) => (
          <tr
            key={idx}
            style={{ backgroundColor: idx % 2 ? "#f8f9fa" : "#fff" }}
          >
            <td style={{ padding: 8, border: "1px solid #ddd" }}>
              {i.item_name}
            </td>
            <td
              style={{
                padding: 8,
                border: "1px solid #ddd",
                textAlign: "center",
              }}
            >
              {i.quantity}
            </td>
            <td
              style={{
                padding: 8,
                border: "1px solid #ddd",
                textAlign: "right",
              }}
            >
              {data.currency}
              {i.unit_price}
            </td>
            <td
              style={{
                padding: 8,
                border: "1px solid #ddd",
                textAlign: "right",
              }}
            >
              {data.currency}
              {i.price}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ textAlign: "right", fontSize: 10 }}>
      <div style={{ marginBottom: 5 }}>
        Subtotal: {data.currency}
        {calculateSubtotal(data.items)}
      </div>
      {data?.discount > 0 && data?.discount_mode !== 'apply_to_items' && (
        <div style={{ marginBottom: 5 }}>
          Discount ({data.discount_type === 'fixed' ? 'Fixed' : data.discount + '%'}): -{data.currency}
          {getDiscountAmount(calculateSubtotal(data.items), data.discount, data.discount_type).toFixed(2)}
        </div>
      )}
      <div style={{ marginBottom: 5 }}>
        Tax ({data.tax}%): {data.currency}
        {calculateTax(data).toFixed(2)}
      </div>
      <div
        style={{
          fontWeight: "bold",
          fontSize: 12,
          backgroundColor: "#2c3e50",
          color: "#fff",
          padding: 5,
        }}
      >
        Total: {data.currency}
        {calculateTotal(data).toFixed(2)}
      </div>
    </div>
    <div style={{ paddingTop: 20 }} dangerouslySetInnerHTML={{ __html: footerContent }} />
  </div>
);
const renderHeader = (data) => (
  <table style={{ width: '100%' }}>
    <tbody>
      <tr>
        <td style={{ width: '50%', verticalAlign: 'top' }}>

          <img src={API_URL + data?.company?.logo} style={{ width: '100px' }} alt="Company Logo" />

          <div>{data?.company?.name || 'R-Wire Limited'}</div>
          <div style={{ width: 140, color: '#777474' }}>{data?.company?.address}</div>
          <div style={{ color: '#535151' }}>VAT reg no: {data?.company?.vat}</div>

          <div>
            <a href={`mailto:${data?.company?.email}`} style={{ textDecoration: 'underline', color: '#535151' }}>
              {data?.company?.email}
            </a>
          </div>

        </td>
        <td style={{ width: '50%', textAlign: 'right', fontSize: 10 }}>
          <div>Issue Date: {convertDateFormat(data?.issue_date)}</div>
        </td>
      </tr>
    </tbody>
  </table>
);

const renderInvoiceTemplate = ({ data, footerContent }) => {

  const calculateSubtotal = () => {
    return data?.items?.reduce((acc, item) => acc + parseFloat(item.price || 0), 0) || 0;
  };

  const calculateDiscountedTotal = () => {
    const { subtotalAfterDiscount } = calculateTaxAmounts(calculateSubtotal(), getEffectiveDiscountPercentage(calculateSubtotal(), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type);
    return subtotalAfterDiscount;
  };

  const calculateTax = () => {
    const { totalTax } = calculateTaxAmounts(calculateSubtotal(), getEffectiveDiscountPercentage(calculateSubtotal(), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type);
    return totalTax;
  };

  const calculateTotal = () => {
    const { total } = calculateTaxAmounts(calculateSubtotal(), getEffectiveDiscountPercentage(calculateSubtotal(), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type);
    return total;
  };
  const currency = data?.currency || 'PKR';
  return (
    <div style={{ fontSize: '12px', color: '#000', padding: 20, background: 'white' }}>
      {renderHeader(data)}
      <div style={{ borderBottom: '3px solid #c75258', marginTop: '10px' }}>
        <p style={{ fontWeight: 600, fontSize: '18px', textAlign: 'center', margin: '10px 0 30px 0' }}>
          QUOTE
        </p>
      </div>
      <table style={{ width: '100%', marginTop: '10px' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <div style={{ fontWeight: 'bold', fontSize: '13px' }}>Bill To:</div>
              <div style={{ fontSize: '12px' }}>{data?.customer_name || ''}</div>
              <div style={{ fontSize: '12px' }}>{data?.phone || ''}</div>
              {data?.email && <div style={{ fontSize: '12px' }}>{data.email}</div>}
              <div style={{ fontSize: '12px', width: '140px' }}>{data?.address || ''}</div>
            </td>
            <td style={{ width: '50%', textAlign: 'right' }}>
              <div style={{ fontSize: '12px' }}>Quote No: {data?.invoice_number || 'RW-058'}</div>
              {data?.order_number && <div style={{ fontSize: '12px' }}>Order No: {data.order_number}</div>}
            </td>
          </tr>
        </tbody>
      </table>
      <table style={{ width: '100%', marginTop: '15px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#c75258', color: 'white', padding: '8px', fontSize: '13px' }}>
            <th style={{ textAlign: 'left', padding: '8px' }}>Description</th>
            <th style={{ textAlign: 'center', padding: '8px' }}>Quantity</th>
            <th style={{ textAlign: 'center', padding: '8px' }}>Unit Price</th>
            <th style={{ textAlign: 'right', padding: '8px' }}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {data?.items?.filter(item => item.item_name && item.price).map((item, index) => (
            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f3f3f3' }}>
              <td style={{ padding: '8px', verticalAlign: 'top', border: '1px dotted #c0c0c0', fontSize: '13px' }}>
                {item.item_name || 'N/A'}

                {(item.description && item.description.replace(/<[^>]*>/g, '').trim() !== '') || (item.color && item.color !== 'null' && item.color !== 'N/A' && item.color.trim() !== '') ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '3px' }}>
                    {item.description && item.description.replace(/<[^>]*>/g, '').trim() !== '' && (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: item.description
                        }}
                      />
                    )}
                    {item.color && item.color !== 'null' && item.color !== 'N/A' && item.color.trim() !== '' && (
                      <span style={{ fontWeight: 'bold', fontSize: '11px', color: '#111827' }}>
                        {item.color}
                      </span>
                    )}
                  </div>
                ) : null}
              </td>
              <td style={{ textAlign: 'center', padding: '8px', border: '1px dotted #c0c0c0', fontSize: '13px', paddingTop: item.description ? 20 : 0 }}>
                {item.quantity || 1}
              </td>
              <td style={{ textAlign: 'center', padding: '8px', border: '1px dotted #c0c0c0', fontSize: '13px', paddingTop: item.description ? 20 : 0 }}>
                {currency}{parseFloat(item.unit_price || 0).toFixed(2)}
              </td>
              <td style={{ textAlign: 'right', padding: '8px', border: '1px dotted #c0c0c0', fontSize: '13px', paddingTop: item.description ? 20 : 0 }}>
                {currency}{parseFloat(item.price || 0).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <table style={{ width: '100%', marginTop: '15px' }}>
        <tbody>
          <tr>
            {/* <td style={{ width: '50%', verticalAlign: 'top', background: '#f2f2f2', border: '2px solid #c75258', textAlign: 'center', padding: '10px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '13px',paddingTop: 0 }}>Payment Instructions</div>
              <div style={{ fontSize: '12px' }}>{data?.company?.accountTitle || 'R-Wire Limited'}</div>
              <div style={{ fontSize: '12px' }}>Sort code: {data?.company?.sortCode || '09-01-29'}</div>
              <div style={{ fontSize: '12px' }}>Account no: {data?.company?.accountNumber || '35103808'}</div>
            </td> */}
            <td style={{ width: '50%', verticalAlign: 'top', textAlign: 'right' }}>
              <div style={{ padding: '5px', background: '#f3f3f3', fontSize: '12px' }}>
                Total Net Amount: {currency}{calculateSubtotal().toFixed(2)}
              </div>
              {data?.discount > 0 && data?.discount_mode !== 'apply_to_items' && (
                <div style={{ padding: '5px', fontSize: '12px' }}>
                  Discount{(data?.discount_type === 'percentage' || data?.discount_type === 'percent' || data?.discount_type !== 'fixed') && data?.discount ? ` (${data.discount}%)` : ''}: {currency}{getDiscountAmount(calculateSubtotal(), data?.discount, data?.discount_type).toFixed(2)}
                </div>
              )}
              {data?.tax > 0 && (
                <div style={{ padding: '5px', fontSize: '12px' }}>
                  Total VAT Amount: {currency}{calculateTax().toFixed(2)}
                </div>
              )}
              {Number(data?.special_less_value) > 0 && (
                <div style={{ padding: '5px', fontSize: '12px' }}>
                  Special Less {data?.less_by ? `By ${data.less_by}` : ''}{(data?.special_less_type === 'percentage' || data?.special_less_type === 'percent') && data?.special_less_value ? ` (${data.special_less_value}%)` : ''}: -{currency}{(calculateTaxAmounts(calculateSubtotal(), getEffectiveDiscountPercentage(calculateSubtotal(), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type).specialDiscountAmount).toFixed(2)}
                </div>
              )}
              <div style={{ border: '1px solid #c75258', margin: '5px 0' }}></div>
              <div style={{ fontWeight: 'bold', padding: '5px', fontSize: '13px' }}>
                Invoice Total: {currency}{calculateTotal().toFixed(2)}
              </div>

              <div style={{ border: '1px solid #c75258', margin: '5px 0' }}></div>

            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ textAlign: 'center' }}>
        <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #d17f15', backgroundColor: '#f9f9f9', textAlign: 'right' }}>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Prepared By: {data?.creator?.name || data?.user_signature?.name || 'Unknown'}</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>{data?.creator?.job_description || data?.user_signature?.title || ''}</div>
          <div style={{ fontSize: '12px', marginTop: '2px' }}>{data?.creator?.phone || data?.user_signature?.phone || ''}</div>
        </div>
        <div style={{ marginTop: '15px' }} dangerouslySetInnerHTML={{ __html: footerContent }} />
        <div style={{ marginTop: '30px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{data?.company?.name || 'R-Wire Limited'}</div>
          <div style={{ fontSize: '12px' }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</div>
          <div style={{ fontSize: '12px' }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</div>
        </div>
      </div>

    </div>
  );
};
// ========== Template 2: Modern Minimal ========== //
const ModernTemplate = ({ data, footerContent }) => (
  <div
    style={{
      fontSize: 11,
      padding: 20,
      backgroundColor: "#fff",
      fontFamily: "Helvetica, sans-serif",
      border: "1px solid #e0e0e0",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 30,
      }}
    >
      <img
        src={API_URL + data.company.logo}
        alt="Logo"
        style={{ width: 180, height: 65, objectFit: "contain" }}
      />
      <h6 style={{ fontSize: 24, color: "#333", fontWeight: 300, margin: 0 }}>
        QUOTE
      </h6>
    </div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 30,
      }}
    >
      <div style={{ fontSize: 10, lineHeight: 1.5 }}>
        <div style={{ fontWeight: "600", fontSize: 11, color: "#666" }}>
          FROM
        </div>
        <div style={{ color: "#333" }}>{data.company.name}</div>
        <div style={{ color: "#666" }}>{data.company.address}</div>
        <div style={{ color: "#666" }}>{data.company.email}</div>
      </div>
      <div style={{ fontSize: 10, textAlign: "right", lineHeight: 1.5 }}>
        <div style={{ fontWeight: "600", fontSize: 11, color: "#666" }}>TO</div>
        <div style={{ color: "#333" }}>{data.customer_name}</div>
        <div style={{ color: "#666" }}>{data.address}</div>
        <div style={{ color: "#666" }}>{data.phone}</div>
        {data.email && <div style={{ color: "#666" }}>{data.email}</div>}
      </div>
    </div>
    <div
      style={{
        backgroundColor: "#f8f9fa",
        padding: 15,
        marginBottom: 20,
        borderLeft: "4px solid #007bff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
        }}
      >
        <div>
          Quote #:{" "}
          <span style={{ fontWeight: "600" }}>{data.invoice_number}</span>
        </div>
        <div>Date: {convertDateFormat(data.issue_date)}</div>
      </div>
    </div>
    <table
      style={{ width: "100%", borderCollapse: "collapse", marginBottom: 30 }}
    >
      <thead>
        <tr>
          <th
            style={{
              padding: 12,
              textAlign: "left",
              borderBottom: "2px solid #007bff",
              fontSize: 10,
              fontWeight: "600",
            }}
          >
            Item
          </th>
          <th
            style={{
              padding: 12,
              textAlign: "center",
              borderBottom: "2px solid #007bff",
              fontSize: 10,
              fontWeight: "600",
            }}
          >
            Qty
          </th>
          <th
            style={{
              padding: 12,
              textAlign: "right",
              borderBottom: "2px solid #007bff",
              fontSize: 10,
              fontWeight: "600",
            }}
          >
            Rate
          </th>
          <th
            style={{
              padding: 12,
              textAlign: "right",
              borderBottom: "2px solid #007bff",
              fontSize: 10,
              fontWeight: "600",
            }}
          >
            Amount
          </th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((i, idx) => (
          <tr key={idx}>
            <td style={{ padding: 10, borderBottom: "1px solid #eee" }}>
              {i.item_name}
            </td>
            <td
              style={{
                padding: 10,
                textAlign: "center",
                borderBottom: "1px solid #eee",
              }}
            >
              {i.quantity}
            </td>
            <td
              style={{
                padding: 10,
                textAlign: "right",
                borderBottom: "1px solid #eee",
              }}
            >
              {data.currency}
              {i.unit_price}
            </td>
            <td
              style={{
                padding: 10,
                textAlign: "right",
                borderBottom: "1px solid #eee",
              }}
            >
              {data.currency}
              {i.price}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ textAlign: "right", fontSize: 10 }}>
      <div style={{ marginBottom: 8, color: "#666" }}>
        Subtotal: {data.currency}
        {calculateSubtotal(data.items)}
      </div>
      <div style={{ marginBottom: 8, color: "#666" }}>
        Tax: {data.currency}
        {calculateTax(data).toFixed(2)}
      </div>
      <div style={{ fontSize: 14, fontWeight: "600", color: "#007bff" }}>
        Total: {data.currency}
        {calculateTotal(data).toFixed(2)}
      </div>
    </div>

    <div dangerouslySetInnerHTML={{ __html: footerContent }} />
  </div>
);

// ========== Template 3: Creative Colorful ========== //
const CreativeTemplate = ({ data, footerContent }) => (
  <div
    style={{
      fontSize: 11,
      padding: 20,
      backgroundColor: "#fff",
      fontFamily: "Arial, sans-serif",
      position: "relative",
    }}
  >
    <style>
      {
        `
        .html p {
          margin-bottom:0px !important;
        }
        `
      }
    </style>
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: 6,
        background: "linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1)",
      }}
    ></div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 25,
        marginTop: 15,
      }}
    >
      <div>
        <img
          src={API_URL + data.company.logo}
          alt="Logo"
          style={{ width: 180, height: 65, objectFit: "contain" }}
        />
        <h6
          style={{
            fontSize: 20,
            color: "#ff6b6b",
            margin: "10px 0 0 0",
            fontWeight: "bold",
          }}
        >
          QUOTE
        </h6>
      </div>
      <div style={{ textAlign: "right", fontSize: 9, color: "#555" }}>
        <div style={{ fontWeight: "bold", color: "#333" }}>
          {data.company.name}
        </div>
        <div>{data.company.address}</div>
        <div>{data.company.email}</div>
      </div>
    </div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 25,
      }}
    >
      <div
        style={{
          backgroundColor: "#ff6b6b",
          color: "#fff",
          padding: 12,
          borderRadius: 8,
          fontSize: 10,
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: 8 }}>BILL TO:</div>
        <div>{data.customer_name}</div>
        <div>{data.phone}</div>
        {data.email && <div>{data.email}</div>}
        <div>{data.address}</div>
      </div>
      <div
        style={{
          backgroundColor: "#4ecdc4",
          color: "#fff",
          padding: 12,
          borderRadius: 8,
          fontSize: 10,
          textAlign: "right",
        }}
      >
        <div>QUOTE: {data.invoice_number}</div>
        <div>Date: {convertDateFormat(data.issue_date)}</div>
      </div>
    </div>
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: 20,
        borderRadius: 8,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <thead>
        <tr
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
          }}
        >
          <th style={{ padding: 10, textAlign: "left" }}>Description</th>
          <th style={{ padding: 10 }}>Qty</th>
          <th style={{ padding: 10 }}>Price</th>
          <th style={{ padding: 10 }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((i, idx) => (
          <tr
            key={idx}
            style={{ backgroundColor: idx % 2 ? "#f8f9ff" : "#fff" }}
          >
            {/* <td style={{ padding: 10 }}>{i.item_name}{'\n'}<div className="html" dangerouslySetInnerHTML={{ __html: i.description }}></div></td> */}
            <td style={{ padding: 10, textAlign: "center" }}>{i.quantity}</td>
            <td style={{ padding: 10, textAlign: "center" }}>
              {data.currency}
              {i.unit_price}
            </td>
            <td style={{ padding: 10, textAlign: "center" }}>
              {data.currency}
              {i.price}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ textAlign: "right", fontSize: 10 }}>
      <div style={{ backgroundColor: "#f8f9ff", padding: 15, borderRadius: 8 }}>
        <div style={{ marginBottom: 5 }}>
          Subtotal: {data.currency}
          {calculateSubtotal(data.items)}
        </div>
        <div style={{ marginBottom: 5 }}>
          Tax: {data.currency}
          {calculateTax(data).toFixed(2)}
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: "bold",
            color: "#667eea",
            borderTop: "2px solid #667eea",
            paddingTop: 8,
            marginTop: 8,
          }}
        >
          Total: {data.currency}
          {calculateTotal(data).toFixed(2)}
        </div>
      </div>
    </div>
    <div style={{ paddingTop: 30 }} dangerouslySetInnerHTML={{ __html: footerContent }} />
  </div>
);

// ========== Template 4: Corporate Elegant ========== //
const CorporateTemplate = ({ data, footerContent }) => (
  <div
    style={{
      fontSize: 11,
      padding: 25,
      backgroundColor: "#fff",
      fontFamily: "Georgia, serif",
      border: "3px double #8b4513",
    }}
  >
    <div
      style={{
        textAlign: "center",
        marginBottom: 25,
        borderBottom: "2px solid #8b4513",
        paddingBottom: 20,
      }}
    >
      <img
        src={API_URL + data.company.logo}
        alt="Logo"
        style={{ width: 180, height: 65, objectFit: 'contain', marginBottom: 10 }}
      />
      <h6
        style={{
          fontSize: 28,
          color: "#8b4513",
          margin: 0,
          fontWeight: "bold",
          letterSpacing: 2,
        }}
      >
        QUOTE
      </h6>
      <div style={{ fontSize: 9, color: "#666", marginTop: 5 }}>
        {data.company.name} â€¢ {data.company.email}
      </div>
    </div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 25,
      }}
    >
      <div
        style={{
          fontSize: 10,
          backgroundColor: "#f5f5dc",
          padding: 15,
          borderLeft: "4px solid #8b4513",
          flex: 1,
          marginRight: 10,
        }}
      >
        <div style={{ fontWeight: "bold", color: "#8b4513", marginBottom: 8 }}>
          QUOTE TO:
        </div>
        <div style={{ fontWeight: "bold" }}>{data.customer_name}</div>
        <div>{data.address}</div>
        <div>{data.phone}</div>
        {data.email && <div>{data.email}</div>}
      </div>
      <div
        style={{
          fontSize: 10,
          backgroundColor: "#f5f5dc",
          padding: 15,
          borderRight: "4px solid #8b4513",
          textAlign: "right",
          flex: 1,
        }}
      >
        <div style={{ fontWeight: "bold", color: "#8b4513" }}>
          QUOTE Details
        </div>
        <div>QUOTE: {data.invoice_number}</div>
        <div>Date: {convertDateFormat(data.issue_date)}</div>
        <div>Order: {data.order_number}</div>
      </div>
    </div>
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: 25,
        border: "2px solid #8b4513",
      }}
    >
      <thead>
        <tr style={{ backgroundColor: "#8b4513", color: "#fff" }}>
          <th
            style={{
              padding: 12,
              textAlign: "left",
              borderRight: "1px solid #fff",
            }}
          >
            Service Description
          </th>
          <th style={{ padding: 12, borderRight: "1px solid #fff", textAlign: 'center' }}>
            Quantity
          </th>
          <th style={{ padding: 12, borderRight: "1px solid #fff", textAlign: 'center' }}>
            Unit Price
          </th>
          <th style={{ padding: 12, textAlign: "right", }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((i, idx) => (
          <tr
            key={idx}
            style={{ backgroundColor: idx % 2 ? "#fafafa" : "#fff" }}
          >
            <td
              style={{
                padding: 10,
                borderBottom: "1px solid #ddd",
                borderRight: "1px solid #ddd",
              }}
            >
              <div style={{ fontWeight: "bold" }}>{i.item_name}</div>
              <div dangerouslySetInnerHTML={{ __html: i.description }} style={{ fontSize: 9, color: "#666" }}></div>
            </td>
            <td
              style={{
                padding: 10,
                textAlign: "center",
                borderBottom: "1px solid #ddd",
                borderRight: "1px solid #ddd",
              }}
            >
              {i.quantity}
            </td>
            <td
              style={{
                padding: 10,
                textAlign: "center",
                borderBottom: "1px solid #ddd",
                borderRight: "1px solid #ddd",
              }}
            >
              {data.currency}
              {i.unit_price}
            </td>
            <td
              style={{
                padding: 10,
                textAlign: "right",
                borderBottom: "1px solid #ddd",
              }}
            >
              {data.currency}
              {i.price}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ textAlign: "right", fontSize: 11 }}>
      <div
        style={{
          backgroundColor: "#f5f5dc",
          padding: 15,
          border: "1px solid #8b4513",
          display: "inline-block",
          minWidth: 200,
        }}
      >
        <div style={{ marginBottom: 8 }}>
          Subtotal: {data.currency}
          {calculateSubtotal(data.items)}
        </div>
        <div style={{ marginBottom: 8 }}>Tax Rate: {data.tax}%</div>
        <div style={{ marginBottom: 8 }}>
          Tax Amount: {data.currency}
          {calculateTax(data).toFixed(2)}
        </div>
        <div
          style={{
            borderTop: "2px solid #8b4513",
            paddingTop: 8,
            fontWeight: "bold",
            fontSize: 14,
            color: "#8b4513",
          }}
        >
          TOTAL DUE: {data.currency}
          {calculateTotal(data).toFixed(2)}
        </div>
      </div>
    </div>
    <div dangerouslySetInnerHTML={{ __html: footerContent }} />
  </div>
);

// ========== Template 5: Tech Futuristic ========== //
const TechTemplate = ({ data, footerContent }) => (
  <div
    style={{
      fontSize: 11,
      padding: 20,
      backgroundColor: "#0f1419",
      color: "#fff",
      fontFamily: "Courier, monospace",
      border: "2px solid #00d4ff",
    }}
  >
    <style>
      {
        `
        .html p {
          margin-bottom:0px !important;
        }
        `
      }
    </style>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20,
        borderBottom: "1px solid #00d4ff",
        paddingBottom: 15,
      }}
    >
      <div>
        <img
          src={API_URL + data.company.logo}
          alt="Logo"
          style={{ width: 160, height: 65, objectFit: 'contain', filter: "invert(1)" }}
        />
        <div
          style={{
            color: "#00d4ff",
            fontSize: 18,
            fontWeight: "bold",
            marginTop: 5,
          }}
        >
          [ INVOICE ]
        </div>
      </div>
      <div style={{ textAlign: "right", fontSize: 9, fontFamily: "monospace" }}>
        <div style={{ color: "#00d4ff" }}>{data.company.name}</div>
        <div>{data.company.address}</div>
        <div>{data.company.email}</div>
      </div>
    </div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 20,
      }}
    >
      <div
        style={{
          fontSize: 10,
          backgroundColor: "#1a2332",
          padding: 12,
          border: "1px solid #00d4ff",
        }}
      >
        <div style={{ color: "#00d4ff", fontWeight: "bold" }}>CLIENT_DATA:</div>
        <div>{data.customer_name}</div>
        <div>{data.address}</div>
        <div>{data.phone}</div>
        {data.email && <div>{data.email}</div>}
      </div>
      <div
        style={{
          fontSize: 10,
          backgroundColor: "#1a2332",
          padding: 12,
          border: "1px solid #00d4ff",
          textAlign: "right",
        }}
      >
        <div style={{ color: "#00d4ff", fontWeight: "bold" }}>
          INVOICE_META:
        </div>
        <div>ID: {data.invoice_number}</div>
        <div>DATE: {convertDateFormat(data.issue_date)}</div>
        <div>ORDER: {data.order_number}</div>
      </div>
    </div>

    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: 20,
        backgroundColor: "#1a2332",
        border: "1px solid #00d4ff",
      }}
    >
      <thead>
        <tr style={{ backgroundColor: "#00d4ff", color: "#0f1419" }}>
          <th
            style={{ padding: 8, textAlign: "left", fontFamily: "monospace" }}
          >
            ITEM.NAME
          </th>
          <th style={{ padding: 8, fontFamily: "monospace" }}>QTY</th>
          <th style={{ padding: 8, fontFamily: "monospace" }}>RATE</th>
          <th style={{ padding: 8, fontFamily: "monospace" }}>TOTAL</th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((i, idx) => (
          <tr key={idx} style={{ borderBottom: "1px solid #2a3441" }}>
            <td style={{ padding: 8 }}>{i.item_name} {'\n'} <div className="html" dangerouslySetInnerHTML={{ __html: i.description }} /></td>
            <td style={{ padding: 8, textAlign: "center" }}>{i.quantity}</td>
            <td style={{ padding: 8, textAlign: "center" }}>
              {data.currency}
              {i.unit_price}
            </td>
            <td style={{ padding: 8, textAlign: "center" }}>
              {data.currency}
              {i.price}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ textAlign: "right", fontSize: 10, fontFamily: "monospace" }}>
      <div
        style={{
          backgroundColor: "#1a2332",
          padding: 12,
          border: "1px solid #00d4ff",
          display: "inline-block",
        }}
      >
        <div>
          SUBTOTAL: {data.currency}
          {calculateSubtotal(data.items)}
        </div>
        <div>TAX_RATE: {data.tax}%</div>
        <div>
          TAX_AMT: {data.currency}
          {calculateTax(data).toFixed(2)}
        </div>
        <div
          style={{
            color: "#00d4ff",
            fontWeight: "bold",
            borderTop: "1px solid #00d4ff",
            paddingTop: 8,
            marginTop: 8,
          }}
        >
          TOTAL_DUE: {data.currency}
          {calculateTotal(data).toFixed(2)}
        </div>
      </div>
    </div>
    <div dangerouslySetInnerHTML={{ __html: footerContent }} />
  </div>
);

// ========== Template 6: Artistic Handwritten ========== //
const ArtisticTemplate = ({ data }) => (
  <div
    style={{
      fontSize: 12,
      padding: 25,
      backgroundColor: "#fffef7",
      fontFamily: "Comic Sans MS, cursive",
      border: "3px solid #d4af37",
      borderRadius: 15,
      position: "relative",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: -2,
        right: -2,
        width: 30,
        height: 30,
        backgroundColor: "#d4af37",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: "bold",
        fontSize: 14,
      }}
    >
      âœ“
    </div>
    <div style={{ textAlign: "center", marginBottom: 25 }}>
      <img
        src={API_URL + data.company.logo}
        alt="Logo"
        style={{ width: 90, height: 45, marginBottom: 10 }}
      />
      <h6
        style={{
          fontSize: 24,
          color: "#8B4513",
          margin: 0,
          textShadow: "2px 2px 4px rgba(139,69,19,0.3)",
          transform: "rotate(-2deg)",
        }}
      >
        QUOTE
      </h6>
      <div
        style={{ fontSize: 10, color: "#8B4513", transform: "rotate(1deg)" }}
      >
        ~ {data.company.name} ~
      </div>
    </div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 25,
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          padding: 15,
          borderRadius: 10,
          boxShadow: "3px 3px 10px rgba(0,0,0,0.1)",
          transform: "rotate(-1deg)",
          fontSize: 11,
        }}
      >
        <div style={{ fontWeight: "bold", color: "#8B4513", marginBottom: 8 }}>
          For:
        </div>
        <div>{data.customer_name}</div>
        <div>{data.address}</div>
        <div>{data.phone}</div>
        {data.email && <div>{data.email}</div>}
      </div>
      <div
        style={{
          backgroundColor: "#fff",
          padding: 15,
          borderRadius: 10,
          boxShadow: "3px 3px 10px rgba(0,0,0,0.1)",
          transform: "rotate(1deg)",
          textAlign: "right",
          fontSize: 11,
        }}
      >
        <div style={{ fontWeight: "bold", color: "#8B4513" }}>
          Invoice Details
        </div>
        <div>#{data.invoice_number}</div>
        <div>{convertDateFormat(data.issue_date)}</div>
      </div>
    </div>
    <div
      style={{
        backgroundColor: "#fff",
        padding: 20,
        borderRadius: 15,
        boxShadow: "5px 5px 15px rgba(0,0,0,0.1)",
        marginBottom: 20,
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th
              style={{
                padding: 10,
                textAlign: "left",
                color: "#8B4513",
                borderBottom: "2px dashed #d4af37",
              }}
            >
              Item
            </th>
            <th
              style={{
                padding: 10,
                color: "#8B4513",
                borderBottom: "2px dashed #d4af37",
              }}
            >
              Qty
            </th>
            <th
              style={{
                padding: 10,
                color: "#8B4513",
                borderBottom: "2px dashed #d4af37",
              }}
            >
              Rate
            </th>
            <th
              style={{
                padding: 10,
                color: "#8B4513",
                borderBottom: "2px dashed #d4af37",
              }}
            >
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((i, idx) => (
            <tr key={idx}>
              <td style={{ padding: 10, borderBottom: "1px dotted #ddd" }}>
                {i.item_name}
              </td>
              <td
                style={{
                  padding: 10,
                  textAlign: "center",
                  borderBottom: "1px dotted #ddd",
                }}
              >
                {i.quantity}
              </td>
              <td
                style={{
                  padding: 10,
                  textAlign: "center",
                  borderBottom: "1px dotted #ddd",
                }}
              >
                {data.currency}
                {i.unit_price}
              </td>
              <td
                style={{
                  padding: 10,
                  textAlign: "center",
                  borderBottom: "1px dotted #ddd",
                }}
              >
                {data.currency}
                {i.price}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div style={{ textAlign: "right", fontSize: 11 }}>
      <div
        style={{
          backgroundColor: "#d4af37",
          color: "#fff",
          padding: 15,
          borderRadius: 10,
          display: "inline-block",
          transform: "rotate(-1deg)",
        }}
      >
        <div style={{ marginBottom: 5 }}>
          Subtotal: {data.currency}
          {calculateSubtotal(data.items)}
        </div>
        <div style={{ marginBottom: 5 }}>
          Tax: {data.currency}
          {calculateTax(data).toFixed(2)}
        </div>
        <div
          style={{
            fontWeight: "bold",
            fontSize: 14,
            borderTop: "2px solid #fff",
            paddingTop: 8,
            marginTop: 8,
          }}
        >
          Total: {data.currency}
          {calculateTotal(data).toFixed(2)}
        </div>
      </div>
    </div>
  </div>
);

// ========== Template 7: Bold Geometric ========== //
const GeometricTemplate = ({ data }) => (
  <div
    style={{
      fontSize: 11,
      padding: 0,
      backgroundColor: "#fff",
      fontFamily: "Helvetica, sans-serif",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        backgroundColor: "#ff4757",
        padding: 20,
        color: "#fff",
        clipPath: "polygon(0 0, 100% 0, 85% 100%, 0% 100%)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <img
            src={API_URL + data.company.logo}
            alt="Logo"
            style={{ width: 80, height: 40, filter: "brightness(0) invert(1)" }}
          />
          <h6
            style={{ fontSize: 20, margin: "10px 0 0 0", fontWeight: "bold" }}
          >
            QUOTE
          </h6>
        </div>
        <div style={{ textAlign: "right", fontSize: 9 }}>
          <div>{data.company.name}</div>
          <div>{data.company.address}</div>
          <div>{data.company.email}</div>
        </div>
      </div>
    </div>
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 25,
        }}
      >
        <div style={{ fontSize: 10 }}>
          <div
            style={{
              backgroundColor: "#2f3542",
              color: "#fff",
              padding: 8,
              marginBottom: 8,
              fontWeight: "bold",
            }}
          >
            BILL TO
          </div>
          <div>{data.customer_name}</div>
          <div>{data.address}</div>
          <div>{data.phone}</div>
          {data.email && <div>{data.email}</div>}
        </div>
        <div style={{ fontSize: 10, textAlign: "right" }}>
          <div
            style={{
              backgroundColor: "#ff6348",
              color: "#fff",
              padding: 8,
              marginBottom: 8,
              fontWeight: "bold",
            }}
          >
            DETAILS
          </div>
          <div>Invoice: {data.invoice_number}</div>
          <div>Date: {convertDateFormat(data.issue_date)}</div>
          <div>Order: {data.order_number}</div>
        </div>
      </div>
      <table
        style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}
      >
        <thead>
          <tr>
            <th
              style={{
                padding: 12,
                textAlign: "left",
                backgroundColor: "#2f3542",
                color: "#fff",
                clipPath: "polygon(0 0, 95% 0, 100% 100%, 0% 100%)",
              }}
            >
              Description
            </th>
            <th
              style={{ padding: 12, backgroundColor: "#ff6348", color: "#fff" }}
            >
              Qty
            </th>
            <th
              style={{ padding: 12, backgroundColor: "#ff6348", color: "#fff" }}
            >
              Price
            </th>
            <th
              style={{
                padding: 12,
                backgroundColor: "#2f3542",
                color: "#fff",
                clipPath: "polygon(0 0, 100% 0, 100% 100%, 5% 100%)",
              }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((i, idx) => (
            <tr
              key={idx}
              style={{ backgroundColor: idx % 2 ? "#f8f9fa" : "#fff" }}
            >
              <td style={{ padding: 10, borderBottom: "1px solid #ddd" }}>
                {i.item_name}
              </td>
              <td
                style={{
                  padding: 10,
                  textAlign: "center",
                  borderBottom: "1px solid #ddd",
                }}
              >
                {i.quantity}
              </td>
              <td
                style={{
                  padding: 10,
                  textAlign: "center",
                  borderBottom: "1px solid #ddd",
                }}
              >
                {data.currency}
                {i.unit_price}
              </td>
              <td
                style={{
                  padding: 10,
                  textAlign: "right",
                  borderBottom: "1px solid #ddd",
                }}
              >
                {data.currency}
                {i.price}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ textAlign: "right", fontSize: 10 }}>
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: 15,
            display: "inline-block",
            clipPath: "polygon(10% 0, 100% 0, 100% 100%, 0% 100%)",
          }}
        >
          <div style={{ marginBottom: 5 }}>
            Subtotal: {data.currency}
            {calculateSubtotal(data.items)}
          </div>
          <div style={{ marginBottom: 5 }}>
            Tax: {data.currency}
            {calculateTax(data).toFixed(2)}
          </div>
          <div style={{ fontWeight: "bold", color: "#ff4757", fontSize: 12 }}>
            Total: {data.currency}
            {calculateTotal(data).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ========== Template 8: Luxury Premium ========== //
const LuxuryTemplate = ({ data }) => (
  <div
    style={{
      fontSize: 11,
      padding: 30,
      backgroundColor: "#1a1a1a",
      color: "#d4af37",
      fontFamily: "Times New Roman, serif",
      border: "4px solid #d4af37",
      position: "relative",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 10,
        left: 10,
        width: 20,
        height: 20,
        border: "2px solid #d4af37",
        transform: "rotate(45deg)",
      }}
    ></div>
    <div
      style={{
        position: "absolute",
        top: 10,
        right: 10,
        width: 20,
        height: 20,
        border: "2px solid #d4af37",
        transform: "rotate(45deg)",
      }}
    ></div>
    <div
      style={{
        position: "absolute",
        bottom: 10,
        left: 10,
        width: 20,
        height: 20,
        border: "2px solid #d4af37",
        transform: "rotate(45deg)",
      }}
    ></div>
    <div
      style={{
        position: "absolute",
        bottom: 10,
        right: 10,
        width: 20,
        height: 20,
        border: "2px solid #d4af37",
        transform: "rotate(45deg)",
      }}
    ></div>

    <div
      style={{
        textAlign: "center",
        marginBottom: 30,
        borderBottom: "3px double #d4af37",
        paddingBottom: 20,
      }}
    >
      <img
        src={API_URL + data.company.logo}
        alt="Logo"
        style={{
          width: 100,
          height: 50,
          marginBottom: 15,
          filter: "sepia(1) hue-rotate(35deg)",
        }}
      />
      <h6
        style={{
          fontSize: 32,
          color: "#d4af37",
          margin: 0,
          fontWeight: "bold",
          letterSpacing: 3,
          textShadow: "2px 2px 4px rgba(212,175,55,0.3)",
        }}
      >
        QUOTE
      </h6>
      <div
        style={{ fontSize: 10, color: "#ccc", marginTop: 5, letterSpacing: 1 }}
      >
        PREMIUM SERVICES
      </div>
    </div>

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 30,
      }}
    >
      <div
        style={{
          fontSize: 10,
          backgroundColor: "#2a2a2a",
          padding: 20,
          border: "2px solid #d4af37",
          flex: 1,
          marginRight: 15,
        }}
      >
        <div
          style={{
            color: "#d4af37",
            fontWeight: "bold",
            marginBottom: 10,
            fontSize: 12,
          }}
        >
          DISTINGUISHED CLIENT
        </div>
        <div style={{ color: "#fff", fontWeight: "bold" }}>
          {data.customer_name}
        </div>
        <div style={{ color: "#ccc" }}>{data.address}</div>
        <div style={{ color: "#ccc" }}>{data.phone}</div>
        {data.email && <div style={{ color: "#ccc" }}>{data.email}</div>}
      </div>
      <div
        style={{
          fontSize: 10,
          backgroundColor: "#2a2a2a",
          padding: 20,
          border: "2px solid #d4af37",
          textAlign: "right",
        }}
      >
        <div
          style={{
            color: "#d4af37",
            fontWeight: "bold",
            marginBottom: 10,
            fontSize: 12,
          }}
        >
          INVOICE PARTICULARS
        </div>
        <div style={{ color: "#fff" }}>
          Invoice Number: {data.invoice_number}
        </div>
        <div style={{ color: "#ccc" }}>
          Issue Date: {convertDateFormat(data.issue_date)}
        </div>
        <div style={{ color: "#ccc" }}>
          Order Reference: {data.order_number}
        </div>
      </div>
    </div>

    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: 30,
        backgroundColor: "#2a2a2a",
        border: "2px solid #d4af37",
      }}
    >
      <thead>
        <tr style={{ backgroundColor: "#d4af37", color: "#1a1a1a" }}>
          <th
            style={{
              padding: 15,
              textAlign: "left",
              fontWeight: "bold",
              fontSize: 11,
            }}
          >
            SERVICE DESCRIPTION
          </th>
          <th style={{ padding: 15, fontWeight: "bold", fontSize: 11 }}>
            QUANTITY
          </th>
          <th style={{ padding: 15, fontWeight: "bold", fontSize: 11 }}>
            UNIT RATE
          </th>
          <th style={{ padding: 15, fontWeight: "bold", fontSize: 11 }}>
            LINE TOTAL
          </th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((i, idx) => (
          <tr key={idx} style={{ borderBottom: "1px solid #444" }}>
            <td style={{ padding: 12, color: "#fff" }}>
              <div style={{ fontWeight: "bold" }}>{i.item_name}</div>
              <div style={{ fontSize: 9, color: "#ccc", marginTop: 2 }}>
                {i.description}
              </div>
            </td>
            <td style={{ padding: 12, textAlign: "center", color: "#d4af37" }}>
              {i.quantity}
            </td>
            <td style={{ padding: 12, textAlign: "center", color: "#d4af37" }}>
              {data.currency}
              {i.unit_price}
            </td>
            <td
              style={{
                padding: 12,
                textAlign: "center",
                color: "#fff",
                fontWeight: "bold",
              }}
            >
              {data.currency}
              {i.price}
            </td>
          </tr>
        ))}
      </tbody>
    </table>

    <div style={{ textAlign: "right", fontSize: 11 }}>
      <div
        style={{
          backgroundColor: "#2a2a2a",
          border: "2px solid #d4af37",
          padding: 20,
          display: "inline-block",
          minWidth: 250,
        }}
      >
        <div style={{ marginBottom: 10, color: "#ccc" }}>
          Subtotal:{" "}
          <span style={{ color: "#fff" }}>
            {data.currency}
            {calculateSubtotal(data.items)}
          </span>
        </div>
        {data?.discount > 0 && data?.discount_mode !== 'apply_to_items' && (
          <div style={{ marginBottom: 10, color: "#ccc" }}>
            Discount ({data.discount_type === 'fixed' ? 'Fixed' : data.discount + '%'}):{" "}
            <span style={{ color: "#fff" }}>
              -{data.currency}
              {getDiscountAmount(calculateSubtotal(data.items), data.discount, data.discount_type).toFixed(2)}
            </span>
          </div>
        )}
        <div style={{ marginBottom: 15, color: "#ccc" }}>
          Tax ({data.tax}%):{" "}
          <span style={{ color: "#fff" }}>
            {data.currency}
            {calculateTax(data).toFixed(2)}
          </span>
        </div>
        <div
          style={{
            borderTop: "3px double #d4af37",
            paddingTop: 15,
            fontSize: 16,
            fontWeight: "bold",
            color: "#d4af37",
            textAlign: "center",
            letterSpacing: 1,
          }}
        >
          TOTAL AMOUNT DUE
          <br />
          <span style={{ fontSize: 20 }}>
            {data.currency}
            {calculateTotal(data).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  </div>
);

// ===== PDF Styles =====
const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 12 },
  section: { marginBottom: 10 },
  header: { fontSize: 16, marginBottom: 10, textAlign: "center" },
  table: { display: "table", width: "auto", marginTop: 10 },
  row: { flexDirection: "row" },
  cell: { flex: 1, borderWidth: 1, borderColor: "#000", padding: 5 },

  companyInfo: {
    width: '50%',
  },
  logo: {
    width: 90,
    marginBottom: 5,
  },
  invoiceTitle: {
    fontSize: 18,
    fontWeight: 600,
    paddingBottom: 30,
    textAlign: 'center',
    marginVertical: 10,
    borderBottom: 3,
    borderBottomColor: '#c75258',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 15,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#c75258',
    color: '#fff',
    fontSize: 12,
    paddingVertical: 5,
    paddingHorizontal: 5
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: 1,
    borderColor: '#c0c0c0',
    padding: 5,
    borderStyle: 'dotted'
  },
  tableColDesc: {
    width: '70%',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColQty: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColUnitPrice: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColPrice: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
  },
  paymentSection: {
    flexDirection: 'row',
    marginTop: 15,
  },
  paymentInstructions: {
    width: '50%',
    backgroundColor: '#f2f2f2',
    borderWidth: 2,
    borderColor: '#c75258',
    textAlign: 'center',
    padding: 5,
    // paddingTop:35
  },
  totals: {
    width: '50%',
    textAlign: 'right',
  },
  totalRow: {
    padding: 5,
    fontSize: 12,
    backgroundColor: '#f3f3f3',
  },
  totalRowBold: {
    padding: 5,
    fontSize: 13,
    fontWeight: 700,
  },
  divider: {
    borderBottom: 1,
    borderColor: '#c75258',
    marginVertical: 5,
  },
  termsSection: {
    marginTop: 15,
    fontSize: 12,
    textAlign: 'center',
  },
  footerLogos: {
    flexDirection: 'row',
    justifyContent: 'center',
    columnGap: 8,
    borderTop: 1,
    borderColor: '#c0c0c0',
    paddingTop: 15,
    marginTop: 40,
  },
  footerLogo: {
    objectFit: 'contain'
  },
  descriptionParagraph: {
    marginBottom: 0,
    fontSize: 9,
    lineHeight: 1.8,
  },
  descriptionBold: {
    fontWeight: 700,
    fontSize: 9,
    lineHeight: 1.8,
  },
  listItem: {
    marginLeft: 5,
    marginBottom: 3,
    fontSize: 9
  },
});

// ========== PDF Template 1: Classic Professional ========== //
const classicStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#fff",
    padding: 20,
    fontSize: 11,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    borderBottomWidth: 3,
    borderBottomColor: "#2c3e50",
    paddingBottom: 15,
  },
  logo: {
    width: 180, height: 65, objectFit: "contain"
  },
  companyInfo: {
    textAlign: "right",
    fontSize: 9,
    color: "#2c3e50",
  },
  companyName: {
    fontWeight: "bold",
    fontSize: 11,
    marginBottom: 2,
  },
  invoiceTitle: {
    textAlign: "center",
    color: "#2c3e50",
    backgroundColor: "#ecf0f1",
    padding: 10,
    marginBottom: 20,
    fontSize: 14,
    fontWeight: "bold",
  },
  billSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  billTo: {
    fontSize: 10,
  },
  billToTitle: {
    fontWeight: "bold",
    marginBottom: 5,
  },
  invoiceDetails: {
    textAlign: "right",
    fontSize: 10,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#2c3e50",
    marginBottom: 20,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableHeader: {
    backgroundColor: "#2c3e50",
    color: "#fff",
  },
  tableCol: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#2c3e50",
  },
  tableCell: {
    margin: "auto",
    marginTop: 8,
    marginBottom: 8,
    fontSize: 10,
  },
  tableCellHeader: {
    margin: "auto",
    marginTop: 8,
    marginBottom: 8,
    fontSize: 10,
    fontWeight: "bold",
    color: "#fff",
  },
  totalsSection: {
    alignItems: "flex-end",
    fontSize: 10,
  },
  totalRow: {
    marginBottom: 5,
  },
  grandTotal: {
    backgroundColor: "#2c3e50",
    color: "#fff",
    padding: 5,
    fontWeight: "bold",
    fontSize: 12,
  },
});

const ClassicPDF = ({ data, footerContent, logoBase64 }) => (
  <Document>
    <Page size="A4" style={classicStyles.page}>
      {/* Header */}
      <View style={classicStyles.header} fixed>
        {data.company.logo && (
          <Image src={logoBase64 || (API_URL + data.company.logo)} style={classicStyles.logo} />
        )}
        <View style={classicStyles.companyInfo}>
          <Text style={classicStyles.companyName}>{data.company.name}</Text>
          <Text>{data.company.address}</Text>
          <Text>VAT: {data.company.vat}</Text>
          <Text>{data.company.email}</Text>
        </View>
      </View>

      {/* Invoice Title */}
      <View style={classicStyles.invoiceTitle}>
        <Text>QUOTE</Text>
      </View>

      {/* Bill To Section */}
      <View style={classicStyles.billSection}>
        <View style={classicStyles.billTo}>
          <Text style={classicStyles.billToTitle}>Bill To:</Text>
          <Text>{data.customer_name}</Text>
          <Text>{data.address}</Text>
          <Text>{data.phone}</Text>
          {data.email && <Text>{data.email}</Text>}
        </View>
        <View style={classicStyles.invoiceDetails}>
          <Text>
            Quote #:{" "}
            <Text style={{ fontWeight: "bold" }}>{data.invoice_number}</Text>
          </Text>
          <Text>Date: {convertDateFormat(data.issue_date)}</Text>
          <Text>Order #: {data.order_number}</Text>
        </View>
      </View>

      {/* Table */}
      <View style={classicStyles.table}>
        {/* Table Header */}
        <View style={[classicStyles.tableRow, classicStyles.tableHeader]}>
          <View style={classicStyles.tableCol}>
            <Text style={classicStyles.tableCellHeader}>Description</Text>
          </View>
          <View style={classicStyles.tableCol}>
            <Text style={classicStyles.tableCellHeader}>Qty</Text>
          </View>
          <View style={classicStyles.tableCol}>
            <Text style={classicStyles.tableCellHeader}>Unit Price</Text>
          </View>
          <View style={classicStyles.tableCol}>
            <Text style={classicStyles.tableCellHeader}>Total</Text>
          </View>
        </View>

        {/* Table Rows */}
        {data.items.map((item, index) => (
          <View key={index} style={classicStyles.tableRow}>
            <View style={classicStyles.tableCol}>
              <Text style={classicStyles.tableCell}>{item.item_name}{'\n'} {parseHtmlToPdf(item?.description)}</Text>
            </View>
            <View style={classicStyles.tableCol}>
              <Text style={classicStyles.tableCell}>{item.quantity}</Text>
            </View>
            <View style={classicStyles.tableCol}>
              <Text style={classicStyles.tableCell}>
                {data.currency}
                {item.unit_price}
              </Text>
            </View>
            <View style={classicStyles.tableCol}>
              <Text style={classicStyles.tableCell}>
                {data.currency}
                {item.price}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={classicStyles.totalsSection}>
        <View style={classicStyles.totalRow}>
          <Text>
            Subtotal: {data.currency}
            {calculateSubtotal(data.items)}
          </Text>
        </View>
        {data?.discount > 0 && data?.discount_mode !== 'apply_to_items' && (
          <View style={classicStyles.totalRow}>
            <Text>
              Discount ({data.discount_type === 'fixed' ? 'Fixed' : data.discount + '%'}): -{data.currency}
              {getDiscountAmount(calculateSubtotal(data.items), data.discount, data.discount_type).toFixed(2)}
            </Text>
          </View>
        )}
        <View style={classicStyles.totalRow}>
          <Text>
            Tax ({data.tax}%): {data.currency}
            {calculateTax(data).toFixed(2)}
          </Text>
        </View>
        <View style={classicStyles.grandTotal}>
          <Text>
            Total: {data.currency}
            {calculateTotal(data).toFixed(2)}
          </Text>
        </View>
      </View>
      <View style={styles.termsSection}>
        {parseHtmlToPdf(footerContent || '')}
        <Text style={{ fontWeight: 'bold', fontSize: 10, marginTop: 15 }}>
          {data?.company?.name || 'Company Name'}
        </Text>
        <Text style={{ fontSize: 10 }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</Text>
        <Text style={{ fontSize: 10 }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</Text>
      </View>




      {/* Page Numbering */}
      <Text style={{ position: 'absolute', bottom: 45, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);

// ========== PDF Template 2: Modern Minimal ========== //
const modernStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#fff",
    padding: 20,
    fontSize: 11,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 30,
  },
  logo: {
    width: 180, height: 65, objectFit: "contain"
  },
  invoiceTitle: {
    fontSize: 20,
    color: "#333",
    fontWeight: "light",
  },
  fromToSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
  },
  fromSection: {
    fontSize: 10,
  },
  toSection: {
    fontSize: 10,
    textAlign: "right",
  },
  sectionTitle: {
    fontWeight: "bold",
    fontSize: 11,
    color: "#666",
    marginBottom: 5,
  },
  infoBox: {
    backgroundColor: "#f8f9fa",
    padding: 15,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#007bff",
  },
  infoBoxContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 10,
  },
  table: {
    marginBottom: 30,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 2,
    borderBottomColor: "#007bff",
    paddingBottom: 12,
    paddingTop: 12,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  tableCol1: { width: "40%" },
  tableCol2: { width: "20%", textAlign: "center" },
  tableCol3: { width: "20%", textAlign: "right" },
  tableCol4: { width: "20%", textAlign: "right" },
  tableHeaderText: {
    fontSize: 10,
    fontWeight: "bold",
  },
  tableCellText: {
    fontSize: 10,
  },
  totalsSection: {
    alignItems: "flex-end",
    fontSize: 10,
  },
  totalRow: {
    marginBottom: 8,
    color: "#666",
  },
  grandTotal: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#007bff",
  },
});

const ModernPDF = ({ data, footerContent, logoBase64 }) => (
  <Document>
    <Page size="A4" style={modernStyles.page}>
      {/* Header */}
      <View style={modernStyles.header} fixed>
        {data.company.logo && (
          <Image src={logoBase64 || (API_URL + data.company.logo)} style={modernStyles.logo} />
        )}
        <Text style={modernStyles.invoiceTitle}>QUOTE</Text>
      </View>

      {/* From/To Section */}
      <View style={modernStyles.fromToSection}>
        <View style={modernStyles.fromSection}>
          <Text style={modernStyles.sectionTitle}>FROM</Text>
          <Text>{data.company.name}</Text>
          <Text style={{ color: "#666" }}>{data.company.address}</Text>
          <Text style={{ color: "#666" }}>{data.company.email}</Text>
        </View>
        <View style={modernStyles.toSection}>
          <Text style={modernStyles.sectionTitle}>TO</Text>
          <Text>{data.customer_name}</Text>
          <Text style={{ color: "#666" }}>{data.address}</Text>
          <Text style={{ color: "#666" }}>{data.phone}</Text>
        </View>
      </View>

      {/* Info Box */}
      <View style={modernStyles.infoBox}>
        <View style={modernStyles.infoBoxContent}>
          <Text>
            Quote #:{" "}
            <Text style={{ fontWeight: "bold" }}>{data.invoice_number}</Text>
          </Text>
          <Text>Date: {convertDateFormat(data.issue_date)}</Text>
        </View>
      </View>

      {/* Table */}
      <View style={modernStyles.table}>
        {/* Table Header */}
        <View style={modernStyles.tableHeader}>
          <View style={modernStyles.tableCol1}>
            <Text style={modernStyles.tableHeaderText}>Item</Text>
          </View>
          <View style={modernStyles.tableCol2}>
            <Text style={modernStyles.tableHeaderText}>Qty</Text>
          </View>
          <View style={modernStyles.tableCol3}>
            <Text style={modernStyles.tableHeaderText}>Rate</Text>
          </View>
          <View style={modernStyles.tableCol4}>
            <Text style={modernStyles.tableHeaderText}>Amount</Text>
          </View>
        </View>

        {/* Table Rows */}
        {data.items.map((item, index) => (
          <View key={index} style={modernStyles.tableRow}>
            <View style={modernStyles.tableCol1}>
              <Text style={modernStyles.tableCellText}>{item.item_name}{'\n'} {parseHtmlToPdf(item?.description)}</Text>
            </View>
            <View style={modernStyles.tableCol2}>
              <Text style={modernStyles.tableCellText}>{item.quantity}</Text>
            </View>
            <View style={modernStyles.tableCol3}>
              <Text style={modernStyles.tableCellText}>
                {data.currency}
                {item.unit_price}
              </Text>
            </View>
            <View style={modernStyles.tableCol4}>
              <Text style={modernStyles.tableCellText}>
                {data.currency}
                {item.price}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={modernStyles.totalsSection}>
        <View style={modernStyles.totalRow}>
          <Text>
            Subtotal: {data.currency}
            {calculateSubtotal(data.items)}
          </Text>
        </View>
        <View style={modernStyles.totalRow}>
          <Text>
            Tax: {data.currency}
            {calculateTax(data).toFixed(2)}
          </Text>
        </View>
        <View style={modernStyles.grandTotal}>
          <Text>
            Total: {data.currency}
            {calculateTotal(data).toFixed(2)}
          </Text>
        </View>
      </View>
      <View style={styles.termsSection}>
        {parseHtmlToPdf(footerContent || '')}
        <Text style={{ fontWeight: 'bold', fontSize: 10, marginTop: 15 }}>
          {data?.company?.name || 'Company Name'}
        </Text>
        <Text style={{ fontSize: 10 }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</Text>
        <Text style={{ fontSize: 10 }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</Text>
      </View>




      {/* Page Numbering */}
      <Text style={{ position: 'absolute', bottom: 15, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>

  </Document>
);

// ========== PDF Template 3: Creative Colorful ========== //
const creativeStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#fff",
    padding: 20,
    fontSize: 11,
    fontFamily: "Helvetica",
  },
  colorStripe: {
    width: "100%",
    height: 6,
    backgroundColor: "#ff6b6b",
    marginBottom: 15,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 25,
  },
  leftHeader: {
    flexDirection: "column",
  },
  logo: {

    width: 180, height: 65, objectFit: "contain",

    marginBottom: 10,
  },
  invoiceTitle: {
    fontSize: 20,
    color: "#ff6b6b",
    fontWeight: "bold",
  },
  companyInfo: {
    textAlign: "right",
    fontSize: 9,
    color: "#555",
  },
  companyName: {
    fontWeight: "bold",
    color: "#333",
    marginBottom: 2,
  },
  billSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 25,
  },
  billToBox: {
    backgroundColor: "#ff6b6b",
    color: "#fff",
    padding: 12,
    borderRadius: 8,
    fontSize: 10,
    width: "45%",
  },
  invoiceBox: {
    backgroundColor: "#4ecdc4",
    color: "#fff",
    padding: 12,
    borderRadius: 8,
    fontSize: 10,
    textAlign: "right",
    width: "45%",
  },
  boxTitle: {
    fontWeight: "bold",
    marginBottom: 8,
  },
  table: {
    marginBottom: 20,
    borderRadius: 8,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#667eea",
    color: "#fff",
    padding: 10,
  },
  tableRow: {
    flexDirection: "row",
    padding: 10,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  tableRowAlt: {
    backgroundColor: "#f8f9ff",
  },
  tableCol: {
    width: "25%",
  },
  tableHeaderText: {
    color: "#fff",
    fontWeight: "bold",
    // textAlign: "center",
  },
  tableCellText: {
    // textAlign: "center",
  },
  totalsBox: {
    alignSelf: "flex-end",
    backgroundColor: "#f8f9ff",
    padding: 15,
    borderRadius: 8,
    fontSize: 10,
  },
  totalRow: {
    marginBottom: 5,
  },
  grandTotal: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#667eea",
    borderTopWidth: 2,
    borderTopColor: "#667eea",
    paddingTop: 8,
    marginTop: 8,
  },
});

const CreativePDF = ({ data, footerContent, logoBase64 }) => (
  <Document>
    <Page size="A4" style={creativeStyles.page}>
      {/* Color Stripe */}
      <View style={creativeStyles.colorStripe} />

      {/* Header */}
      <View style={creativeStyles.header} fixed>
        <View style={creativeStyles.leftHeader}>
          {data.company.logo && (
            <Image src={logoBase64 || (API_URL + data.company.logo)} style={creativeStyles.logo} />
          )}
          <Text style={creativeStyles.invoiceTitle}>QUOTE</Text>
        </View>
        <View style={creativeStyles.companyInfo}>
          <Text style={creativeStyles.companyName}>{data.company.name}</Text>
          <Text>{data.company.address}</Text>
          <Text>{data.company.email}</Text>
        </View>
      </View>

      {/* Bill Section */}
      <View style={creativeStyles.billSection}>
        <View style={creativeStyles.billToBox}>
          <Text style={creativeStyles.boxTitle}>BILL TO:</Text>
          <Text>{data.customer_name}</Text>
          <Text>{data.address}</Text>
        </View>
        <View style={creativeStyles.invoiceBox}>
          <Text>QUOTE: {data.invoice_number}</Text>
          <Text>Date: {convertDateFormat(data.issue_date)}</Text>
        </View>
      </View>

      {/* Table */}
      <View style={creativeStyles.table}>
        {/* Table Header */}
        <View style={creativeStyles.tableHeader}>
          <View style={creativeStyles.tableCol}>
            <Text style={creativeStyles.tableHeaderText}>Description</Text>
          </View>
          <View style={creativeStyles.tableCol}>
            <Text style={creativeStyles.tableHeaderText}>Qty</Text>
          </View>
          <View style={creativeStyles.tableCol}>
            <Text style={creativeStyles.tableHeaderText}>Price</Text>
          </View>
          <View style={creativeStyles.tableCol}>
            <Text style={creativeStyles.tableHeaderText}>Total</Text>
          </View>
        </View>

        {/* Table Rows */}
        {data.items.map((item, index) => (
          <View
            key={index}
            style={[
              creativeStyles.tableRow,
              index % 2 ? creativeStyles.tableRowAlt : null,
            ]}
          >
            <View style={creativeStyles.tableCol}>
              <Text style={creativeStyles.tableCellText}>{item.item_name}{`\n`}{parseHtmlToPdf(item.description)}</Text>
            </View>
            <View style={creativeStyles.tableCol}>
              <Text style={creativeStyles.tableCellText}>{item.quantity}</Text>
            </View>
            <View style={creativeStyles.tableCol}>
              <Text style={creativeStyles.tableCellText}>
                {data.currency}
                {item.unit_price}
              </Text>
            </View>
            <View style={creativeStyles.tableCol}>
              <Text style={creativeStyles.tableCellText}>
                {data.currency}
                {item.price}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={creativeStyles.totalsBox}>
        <View style={creativeStyles.totalRow}>
          <Text>
            Subtotal: {data.currency}
            {calculateSubtotal(data.items)}
          </Text>
        </View>
        <View style={creativeStyles.totalRow}>
          <Text>
            Tax: {data.currency}
            {calculateTax(data).toFixed(2)}
          </Text>
        </View>
        <View style={creativeStyles.grandTotal}>
          <Text>
            Total: {data.currency}
            {calculateTotal(data).toFixed(2)}
          </Text>
        </View>
      </View>
      <View style={styles.termsSection}>
        {parseHtmlToPdf(footerContent || '')}
        <Text style={{ fontWeight: 'bold', fontSize: 10, marginTop: 15 }}>
          {data?.company?.name || 'Company Name'}
        </Text>
        <Text style={{ fontSize: 10 }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</Text>
        <Text style={{ fontSize: 10 }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</Text>
      </View>




      {/* Page Numbering */}
      <Text style={{ position: 'absolute', bottom: 15, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);

// ========== PDF Template 4: Corporate Elegant ========== //
const corporateStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#fff",
    padding: 25,
    fontSize: 11,
    fontFamily: "Times-Roman",
    borderWidth: 3,
    borderColor: "#8b4513",
    borderStyle: "double",
  },
  header: {
    textAlign: "center",
    marginBottom: 25,
    borderBottomWidth: 2,
    borderBottomColor: "#8b4513",
    paddingBottom: 20,
  },
  logo: {
    width: 180,
    height: 65,
    marginBottom: 10,
    objectFit: 'contain'
  },
  invoiceTitle: {
    fontSize: 28,
    color: "#8b4513",
    fontWeight: "bold",
    letterSpacing: 2,
    marginBottom: 5,
  },
  companyEmail: {
    fontSize: 9,
    color: "#666",
  },
  billSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 25,
  },
  billToBox: {
    backgroundColor: "#f5f5dc",
    padding: 15,
    borderLeftWidth: 4,
    borderLeftColor: "#8b4513",
    width: "48%",
  },
  invoiceDetailsBox: {
    backgroundColor: "#f5f5dc",
    padding: 15,
    borderRightWidth: 4,
    borderRightColor: "#8b4513",
    textAlign: "right",
    width: "48%",
  },
  boxTitle: {
    fontWeight: "bold",
    color: "#8b4513",
    marginBottom: 8,
  },
  customerName: {
    fontWeight: "bold",
    marginBottom: 2,
  },
  table: {
    borderWidth: 2,
    borderColor: "#8b4513",
    marginBottom: 25,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#8b4513",
    color: "#fff",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  tableRowAlt: {
    backgroundColor: "#fafafa",
  },
  tableCol1: {
    width: "40%",
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: "#fff",
  },
  tableCol2: {
    width: "20%",
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: "#fff",
  },
  tableCol3: {
    width: "20%",
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: "#fff",
  },
  tableCol4: { width: "20%", padding: 12 },
  tableHeaderText: {
    color: "#fff",
    fontWeight: "bold",
  },
  itemName: {
    fontWeight: "bold",
    marginBottom: 2,
  },
  itemDescription: {
    fontSize: 9,
    color: "#666",
  },
  totalsBox: {
    alignSelf: "flex-end",
    backgroundColor: "#f5f5dc",
    padding: 15,
    borderWidth: 1,
    borderColor: "#8b4513",
    minWidth: 200,
  },
  totalRow: {
    marginBottom: 8,
  },
  grandTotal: {
    borderTopWidth: 2,
    borderTopColor: "#8b4513",
    paddingTop: 8,
    fontWeight: "bold",
    fontSize: 14,
    color: "#8b4513",
  },
});

const CorporatePDF = ({ data, footerContent, logoBase64 }) => (
  <Document>
    <Page size="A4" style={corporateStyles.page}>
      {/* Header */}
      <View style={corporateStyles.header} fixed>
        {data.company.logo && (
          <Image src={logoBase64 || (API_URL + data.company.logo)} style={corporateStyles.logo} />
        )}
        <Text style={corporateStyles.invoiceTitle}>QUOTE</Text>
        <Text style={corporateStyles.companyEmail}>
          {data.company.name} â€¢ {data.company.email}
        </Text>
      </View>

      {/* Bill Section */}
      <View style={corporateStyles.billSection}>
        <View style={corporateStyles.billToBox}>
          <Text style={corporateStyles.boxTitle}>QUOTE TO:</Text>
          <Text style={corporateStyles.customerName}>{data.customer_name}</Text>
          <Text>{data.address}</Text>
          <Text>{data.phone}</Text>
          {data.email && <Text>{data.email}</Text>}
        </View>
        <View style={corporateStyles.invoiceDetailsBox}>
          <Text style={corporateStyles.boxTitle}>QUOTE Details</Text>
          <Text>Number: {data.invoice_number}</Text>
          <Text>Date: {convertDateFormat(data.issue_date)}</Text>
          {
            data.order_number && (
              <Text>Order: {data.order_number}</Text>
            )
          }
        </View>
      </View>

      {/* Table */}
      <View style={corporateStyles.table}>
        {/* Table Header */}
        <View style={corporateStyles.tableHeader}>
          <View style={corporateStyles.tableCol1}>
            <Text style={corporateStyles.tableHeaderText}>
              Service Description
            </Text>
          </View>
          <View style={corporateStyles.tableCol2}>
            <Text style={corporateStyles.tableHeaderText}>Quantity</Text>
          </View>
          <View style={corporateStyles.tableCol3}>
            <Text style={corporateStyles.tableHeaderText}>Unit Price</Text>
          </View>
          <View style={corporateStyles.tableCol4}>
            <Text style={corporateStyles.tableHeaderText}>Line Total</Text>
          </View>
        </View>

        {/* Table Rows */}
        {data.items.map((item, index) => (
          <View
            key={index}
            style={[
              corporateStyles.tableRow,
              index % 2 ? corporateStyles.tableRowAlt : null,
            ]}
          >
            <View
              style={[corporateStyles.tableCol1, { borderRightColor: "#ddd" }]}
            >
              <Text style={corporateStyles.itemName}>{item.item_name}</Text>
              <Text style={corporateStyles.itemDescription}>
                {parseHtmlToPdf(item.description)}
              </Text>
            </View>
            <View
              style={[
                corporateStyles.tableCol2,
                { borderRightColor: "#ddd", textAlign: "center" },
              ]}
            >
              <Text>{item.quantity}</Text>
            </View>
            <View
              style={[
                corporateStyles.tableCol3,
                { borderRightColor: "#ddd", textAlign: "right" },
              ]}
            >
              <Text>
                {data.currency}
                {item.unit_price}
              </Text>
            </View>
            <View style={[corporateStyles.tableCol4, { textAlign: "right" }]}>
              <Text>
                {data.currency}
                {item.price}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={corporateStyles.totalsBox}>
        <View style={corporateStyles.totalRow}>
          <Text>
            Subtotal: {data.currency}
            {calculateSubtotal(data.items)}
          </Text>
        </View>
        <View style={corporateStyles.totalRow}>
          <Text>Tax Rate: {data.tax}%</Text>
        </View>
        <View style={corporateStyles.totalRow}>
          <Text>
            Tax Amount: {data.currency}
            {calculateTax(data).toFixed(2)}
          </Text>
        </View>
        <View style={corporateStyles.grandTotal}>
          <Text>
            TOTAL DUE: {data.currency}
            {calculateTotal(data).toFixed(2)}
          </Text>
        </View>
      </View>
      <View style={styles.termsSection}>
        {parseHtmlToPdf(footerContent || '')}
        <Text style={{ fontWeight: 'bold', fontSize: 10, marginTop: 15 }}>
          {data?.company?.name || 'Company Name'}
        </Text>
        <Text style={{ fontSize: 10 }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</Text>
        <Text style={{ fontSize: 10 }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</Text>
      </View>




      {/* Page Numbering */}
      <Text style={{ position: 'absolute', bottom: 15, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);


const stylesCoppergat = StyleSheet.create({
  page: {
    paddingTop: 0,
    paddingHorizontal: 20,
    paddingBottom: 42,
    fontSize: 11,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
  },

  topBanner: {
    backgroundColor: "#c8102e",
    color: "#fff",
    textAlign: "center",
    paddingVertical: 7,
    fontSize: 20,
    fontWeight: "bold",
    letterSpacing: 20,
    marginHorizontal: -20,
  },


  logoWrapper: {
    textAlign: "left",
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: 15,
    marginBottom: 0,
  },

  logoImage: {
    width: 215,
    height: 58,
    objectFit: "contain",
    alignSelf: "flex-start",
    marginLeft: -25,
  },

  headerSection: {
    textAlign: "center",
    paddingTop: 2,
    paddingBottom: 4,
    alignItems: "center",
  },

  headerBadgeContainer: { flexDirection: "row", alignItems: "center", border: "2px solid #222", paddingVertical: 6, paddingHorizontal: 16 },
  headerBadgeC: { width: 38, height: 38, border: "3px solid #c8102e", borderRadius: 19, flexDirection: "row", alignItems: "center", justifyContent: "center" },
  headerBadgeCText: { color: "#c8102e", fontSize: 18, fontWeight: 900 },
  headerBadgeTextWrapper: { marginLeft: 8 },
  headerBadgeTitle: { fontSize: 22, fontWeight: 900, letterSpacing: 2 },
  headerBadgeSub: { fontSize: 9, color: "#555" },
  headerBadgeRight: { fontSize: 9, letterSpacing: 5, color: "#555", textAlign: "right" },
  quotationTitle: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 13,
    marginTop: 0,
    marginBottom: 8,
  },



  contentPadding: {
    paddingHorizontal: 0,
  },

  customerBox: { borderWidth: 1.5, borderColor: "#000" },
  customerHeader: { backgroundColor: "#000", height: "15px", color: "#fff", textAlign: "center", fontWeight: "bold", padding: 4, fontSize: 11 },
  customerTable: { flexDirection: "column" },
  customerRow: { flexDirection: "row", borderTopWidth: 1, borderColor: "#000", minHeight: 20 },
  customerLabel: { width: 90, fontWeight: "bold", paddingHorizontal: 4, paddingVertical: 2, borderRightWidth: 1, borderColor: "#000", fontSize: 10 },
  customerValueWhite: { flex: 1, paddingHorizontal: 4, paddingVertical: 2, fontSize: 10 },
  customerValueYellow: { flex: 1, paddingHorizontal: 4, paddingVertical: 2, fontSize: 10 },
  dateLabel: { width: 60, fontWeight: "bold", paddingHorizontal: 4, paddingVertical: 2, borderLeftWidth: 1, borderRightWidth: 1, borderColor: "#000", fontSize: 10 },
  dateValue: { width: 90, paddingHorizontal: 4, paddingVertical: 2, fontSize: 10 },

  introText: {
    paddingVertical: 8,
    paddingHorizontal: 0,
    fontSize: 11,
  },
  detailsHeader: { border: "1.5px solid #222", textAlign: "center", fontWeight: "bold", fontSize: 12, padding: 4, backgroundColor: "#f5f5f5" },

  tableWrapper: { width: "100%", fontSize: 10.5, borderLeft: "1px solid #ccc", borderTop: "1px solid #ccc" },
  tableRow: { flexDirection: "row" },
  th: { backgroundColor: "#222", color: "#fff", padding: 3, borderRight: "1px solid #444", borderBottom: "1px solid #444", textAlign: "center" },
  thLeft: { backgroundColor: "#222", color: "#fff", padding: 3, borderRight: "1px solid #444", borderBottom: "1px solid #444", textAlign: "left" },
  tdCenter: { paddingVertical: 2, paddingHorizontal: 4, borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc", textAlign: "center" },
  tdStyle: { paddingVertical: 2, paddingHorizontal: 4, borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" },
  tdRight: { paddingVertical: 2, paddingHorizontal: 4, borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc", textAlign: "right" },

  tdCenterYellow: { paddingVertical: 2, paddingHorizontal: 4, borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc", textAlign: "center", fontWeight: "bold" },

  totalsTable: { width: "100%", marginTop: 10, borderLeft: "1.5px solid #222", borderTop: "1.5px solid #222" },
  totalRow: { flexDirection: "row" },
  totalLabel: { paddingVertical: 3, paddingHorizontal: 10, borderRight: "1.5px solid #222", borderBottom: "1.5px solid #222", fontWeight: "bold", flex: 1 },
  totalValue: { paddingVertical: 3, paddingHorizontal: 10, borderRight: "1.5px solid #222", borderBottom: "1.5px solid #222", textAlign: "right", width: 180, fontWeight: "bold" },
  totalYellow: {},

  termsWrapper: {
    marginHorizontal: 0,
    marginTop: 20,
  },
  termsHeader: { border: "1.5px solid #222", fontWeight: "bold", fontSize: 11, textAlign: "center", padding: 4 },
  termsTable: { width: "100%", fontSize: 9, borderLeft: "1.5px solid #222" },
  termsRow: { flexDirection: "row" },
  termsLabel: { borderRight: "1.5px solid #222", borderBottom: "1.5px solid #222", paddingVertical: 3, paddingHorizontal: 6, fontWeight: "bold", width: 130 },
  termsValue: { borderRight: "1.5px solid #222", borderBottom: "1.5px solid #222", paddingVertical: 3, paddingHorizontal: 6, flex: 1 },
  termsYellow: {},

  outroText: {
    paddingVertical: 4,
    paddingHorizontal: 0,
    paddingBottom: 8,
    fontSize: 11,
    marginTop: 10,
  },
  badgesContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 20,
    paddingBottom: 6,
  },
  badgeCenterGroup: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 15,
  },
  badgeKema: { height: 85, objectFit: "contain" },
  badgeTuv: { height: 35, objectFit: "contain" },
  badgeAlon: { height: 35, objectFit: "contain" },
  badgeSap: { height: 35, objectFit: "contain", marginLeft: 10, marginRight: -6 },
  badgePsqca: { height: 48, objectFit: "contain" },
  badgePkstand: { height: 48, objectFit: "contain" },
  badgeCe: { height: 35, objectFit: "contain" },

  signatureBlock: {
    paddingTop: 6,
    paddingHorizontal: 0,
    paddingBottom: 35,
    fontSize: 11,
    lineHeight: 1.6,
  },
  signatureTitle: { fontWeight: "bold", fontSize: 12 },
  signatureBold: { fontWeight: "bold" },
  signatureEmail: { color: "#0000cc" },

  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#c8102e",
    color: "#fff",
    textAlign: "center",
    padding: 10,
    fontSize: 12,
    fontWeight: "bold",
  },
});

const CoppergatTemplate = ({ data, footerContent, logoBase64 }) => {
  const signature = parseUserSignature(data.user_signature, data.creator || data.user);
  const terms = parseTermsConditions(data.terms_conditions);
  const isApplyToItems = (data?.discount_mode === 'apply_to_items' || data?.discountMode === 'apply_to_items') && Number(data?.discount) > 0;
  const rawSubtotal = (data?.items || []).reduce((sum, item) => sum + (parseFloat(item.price) || (parseFloat(item.quantity || item.qty || 0) * parseFloat(item.unit_price || 0)) || 0), 0);
  const effDiscountPct = getEffectiveDiscountPercentage(rawSubtotal, data?.discount, data?.discount_type);

  const displayItems = isApplyToItems
    ? (data?.items || []).map(item => {
        const qty = parseFloat(item.quantity || item.qty || 0);
        const originalUnitPrice = parseFloat(item.unit_price || 0);
        const effectiveUnitPrice = Math.round(originalUnitPrice * (1 - effDiscountPct / 100));
        const effectivePrice = Math.round(qty * effectiveUnitPrice);
        return {
          ...item,
          unit_price: effectiveUnitPrice,
          price: effectivePrice,
        };
      })
    : (data?.items || []).map(item => {
        const qty = parseFloat(item.quantity || item.qty || 0);
        const unitPrice = Math.round(parseFloat(item.unit_price || 0));
        const disc = Number(item.discount || 0);
        const discType = item.discount_type || item.discountType || 'percentage';
        return {
          ...item,
          price: calculateItemPrice(qty, unitPrice, disc, discType),
        };
      });

  const subtotal = displayItems.reduce(
    (sum, item) => sum + (parseFloat(item.price) || 0),
    0
  );

  const specialLessValue = Number(data?.special_less_value) || 0;
  const specialLessType = data?.special_less_type || 'fixed';
  const lessby = (data?.less_by) || 0;
  const { gstAmount: gst, vatAmount: tax, total: netPayable } = calculateTaxAmounts(
    subtotal,
    isApplyToItems ? 0 : effDiscountPct,
    data?.gst,
    data?.tax,
    specialLessValue,
    specialLessType
  );

  return (
    <Document>
      <Page size="A4" style={stylesCoppergat.page}>
        {/* Top Banner */}
        {/* <View style={stylesCoppergat.topBanner} fixed>
          <Text>COPPERGAT CABLES</Text>
        </View> */}

        {/* Logo */}
        {data?.company?.logo && (
          <View style={[stylesCoppergat.logoWrapper, { position: 'relative' }]} fixed>
            <Image src={logoBase64 || (API_URL + data.company.logo)} style={stylesCoppergat.logoImage} />
            <View style={{ position: 'absolute', right: 0, top: 10 }}>
              <Image src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=Verified%20by%20Coppergat" style={{ width: 60, height: 60 }} />
            </View>
          </View>
        )}

        <View style={{ width: '100%', height: '75vh', justifyContent: 'center', alignItems: 'center' }}>
          <Image src={splashImg} style={{ width: '90%', objectFit: 'contain' }} />
        </View>
        <View style={stylesCoppergat.headerSection} break>
          <Text style={stylesCoppergat.quotationTitle}>QUOTATION SHEET</Text>
        </View>

        {/* Customer Info */}
        <View style={stylesCoppergat.contentPadding}>
          <View style={stylesCoppergat.customerBox}>
            <Text style={stylesCoppergat.customerHeader}></Text>
            <View style={stylesCoppergat.customerTable}>
              <View style={stylesCoppergat.customerRow}>
                <Text style={stylesCoppergat.customerLabel}>Ref No:</Text>
                <Text style={stylesCoppergat.customerValueWhite}>{data.invoice_number}</Text>
              </View>
              <View style={stylesCoppergat.customerRow}>
                <Text style={stylesCoppergat.customerLabel}>Company Name:</Text>
                <Text style={stylesCoppergat.customerValueYellow}>{data.customer_name}</Text>
                <Text style={stylesCoppergat.dateLabel}>Dated:</Text>
                <Text style={stylesCoppergat.dateValue}>{new Date(data.issue_date).toLocaleDateString("en-GB")}</Text>
              </View>
              <View style={stylesCoppergat.customerRow}>
                <Text style={stylesCoppergat.customerLabel}>Attn To:</Text>
                <Text style={stylesCoppergat.customerValueYellow}>{data?.attn_to}</Text>
              </View>
              <View style={stylesCoppergat.customerRow}>
                <Text style={stylesCoppergat.customerLabel}>Address:</Text>
                <Text style={stylesCoppergat.customerValueYellow}>{data.address}</Text>
              </View>
              <View style={stylesCoppergat.customerRow}>
                <Text style={stylesCoppergat.customerLabel}>Contact:</Text>
                <Text style={stylesCoppergat.customerValueYellow}>{data.phone}</Text>
              </View>
              <View style={stylesCoppergat.customerRow}>
                <Text style={stylesCoppergat.customerLabel}>E-mail:</Text>
                <Text style={stylesCoppergat.customerValueYellow}>{data.email || ''}</Text>
              </View>
              <View style={stylesCoppergat.customerRow}>
                <Text style={stylesCoppergat.customerLabel}>Note:</Text>
                <Text style={[stylesCoppergat.customerValueYellow, { fontWeight: "bold" }]}>{data.note || ''}</Text>
              </View>
            </View>
          </View>
        </View>

        <Text style={stylesCoppergat.introText}>
          We are pleased to submit our most competitive price of cables as under:
        </Text>

        {/* Quotation Details */}
        {/* <View style={stylesCoppergat.contentPadding}>
          <Text style={stylesCoppergat.detailsHeader}>QUOTATION DETAILS</Text>
        </View> */}

        {/* Items Table */}
        <View style={stylesCoppergat.contentPadding}>
          <View style={stylesCoppergat.tableWrapper}>
            <View style={stylesCoppergat.tableRow}>
              <Text style={[stylesCoppergat.th, { width: "7%" }]}>Sr No.</Text>
              <Text style={[stylesCoppergat.thLeft, { width: "39%" }]}>Description</Text>
              <Text style={[stylesCoppergat.th, { width: "8%" }]}>Uom</Text>
              <Text style={[stylesCoppergat.th, { width: "10%" }]}>Quantity</Text>
              <Text style={[stylesCoppergat.th, { width: "12%" }]}>
                Rate ({data.currency})
              </Text>
              <Text style={[stylesCoppergat.th, { width: "10%" }]}>Disc</Text>
              <Text style={[stylesCoppergat.th, { width: "14%" }]}>Total</Text>
            </View>
            {displayItems.map((item, index) => (
              <View key={index} style={stylesCoppergat.tableRow}>
                <Text style={[stylesCoppergat.tdCenter, { width: "7%" }]}>{index + 1}</Text>
                <View style={[stylesCoppergat.tdStyle, { width: "39%" }]}>
                  <Text>{item.item_name}</Text>
                  {(item.description && item.description.replace(/<[^>]*>/g, '').trim() !== '') || (item.color && item.color !== 'null' && item.color !== 'N/A' && item.color.trim() !== '') ? (
                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                      {item.description && item.description.replace(/<[^>]*>/g, '').trim() !== '' ? parseHtmlToPdf(item.description) : null}
                      {item.color && item.color !== 'null' && item.color !== 'N/A' && item.color.trim() !== '' ? (
                        <Text style={{ fontSize: 8.5, color: "#111827", fontWeight: "bold", lineHeight: 1.3 }}>
                          {item.color}
                        </Text>
                      ) : null}
                    </View>
                  ) : null}
                </View>
                <Text style={[stylesCoppergat.tdCenter, { width: "8%" }]}>{item.uom}</Text>
                <Text style={[stylesCoppergat.tdCenterYellow, { width: "10%" }]}>{item.quantity}</Text>
                <Text style={[stylesCoppergat.tdRight, { width: "12%" }]}>{item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== '' ? Math.round(parseFloat(item.unit_price)).toLocaleString() : '0'}</Text>
                <Text style={[stylesCoppergat.tdCenter, { width: "10%" }]}>
                  {Number(item.discount || 0) > 0 ? `${item.discount}${item.discount_type === 'fixed' ? ' PKR' : '%'}` : '-'}
                </Text>
                <Text style={[stylesCoppergat.tdRight, { width: "14%" }]}>{Math.round(parseFloat(item.price || 0)).toLocaleString()}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Totals Block */}
        <View wrap={false} style={[stylesCoppergat.contentPadding, { marginTop: 10 }]}>
          <View style={stylesCoppergat.totalsTable}>
            <View style={stylesCoppergat.totalRow}>
              <Text style={stylesCoppergat.totalLabel}>Total</Text>
              <Text style={stylesCoppergat.totalValue}> {Math.round(subtotal).toLocaleString()}</Text>
            </View>
            {data?.discount > 0 && data?.discount_mode !== 'apply_to_items' && (<>
              <View style={stylesCoppergat.totalRow}>
                <Text style={stylesCoppergat.totalLabel}>Discount{(data?.discount_type === 'percentage' || data?.discount_type === 'percent' || data?.discount_type !== 'fixed') && data?.discount ? ` (${data.discount}%)` : ''}</Text>
                <Text style={stylesCoppergat.totalValue}>
                  {Math.round(getDiscountAmount(subtotal, data?.discount, data?.discount_type)).toLocaleString()}
                </Text>
              </View>
            </>)}
            {specialLessValue > 0 && (
              <View style={stylesCoppergat.totalRow}>
                <Text style={stylesCoppergat.totalLabel}>Special Less{lessby ? ` By ${lessby}` : ''}{(specialLessType === 'percentage' || specialLessType === 'percent') && specialLessValue ? ` (${specialLessValue}%)` : ''}</Text>
                <Text style={stylesCoppergat.totalValue}>
                  {Math.round(calculateTaxAmounts(subtotal, getEffectiveDiscountPercentage(subtotal, data?.discount, data?.discount_type), data?.gst, data?.tax, specialLessValue, specialLessType).specialDiscountAmount).toLocaleString()}
                </Text>
              </View>
            )}
            {data.gst > 0 && <>
              <View style={stylesCoppergat.totalRow}>
                <Text style={[stylesCoppergat.totalLabel, stylesCoppergat.totalYellow]}>GST {data.gst}%</Text>
                <Text style={[stylesCoppergat.totalValue, stylesCoppergat.totalYellow]}>{Math.round(gst).toLocaleString()}</Text>
              </View>
            </>}
            {data.tax > 0 && <>
              <View style={stylesCoppergat.totalRow}>
                <Text style={[stylesCoppergat.totalLabel, stylesCoppergat.totalYellow]}>Additional GST {data.tax}%</Text>
                <Text style={[stylesCoppergat.totalValue, stylesCoppergat.totalYellow]}>{Math.round(tax).toLocaleString()}</Text>
              </View>
            </>}

            <View style={stylesCoppergat.totalRow}>
              <Text style={stylesCoppergat.totalLabel}>Net Payable</Text>
              <Text style={stylesCoppergat.totalValue}>{Math.round(netPayable).toLocaleString()}</Text>
            </View>

          </View>
        </View>

        {data?.items?.length > 0 && (
          <View wrap={false}>
            {/* Badges */}
            <View style={stylesCoppergat.badgesContainer}>
              <Image src={psqcaImg} style={stylesCoppergat.badgePsqca} />
              <Image src={tuvImg} style={stylesCoppergat.badgeTuv} />
              <Image src={sapImg} style={stylesCoppergat.badgeSap} />
              <Image src={kemaImg} style={stylesCoppergat.badgeKema} />
              <Image src={alonImg} style={stylesCoppergat.badgeAlon} />
              <Image src={pkstandImg} style={stylesCoppergat.badgePkstand} />
              <Image src={ceImg} style={stylesCoppergat.badgeCe} />
            </View>

            {/* Signature */}
            {renderCoppergatSignaturePdf(signature, stylesCoppergat)}
          </View>
        )}

        <View break>
          {/* Terms Title */}
          <View>
            {renderCoppergatTermsPdf(terms, stylesCoppergat)}
          </View>

          <Text style={stylesCoppergat.outroText}>Looking forward to a prosperous business relationship. If you have any queries feel free to contact us.</Text>

          {/* Badges */}
          <View style={stylesCoppergat.badgesContainer}>
            <Image src={psqcaImg} style={stylesCoppergat.badgePsqca} />
            <Image src={tuvImg} style={stylesCoppergat.badgeTuv} />
            <Image src={sapImg} style={stylesCoppergat.badgeSap} />
            <Image src={kemaImg} style={stylesCoppergat.badgeKema} />
            <Image src={alonImg} style={stylesCoppergat.badgeAlon} />
            <Image src={pkstandImg} style={stylesCoppergat.badgePkstand} />
            <Image src={ceImg} style={stylesCoppergat.badgeCe} />
          </View>

          {/* Signature */}
          {renderCoppergatSignaturePdf(signature, stylesCoppergat)}
        </View>

        {/* Footer */}
        <View style={stylesCoppergat.footer} fixed>
          <Text>109-FF, Divine Mega 2, Airport Road, Lahore - Pakistan   UAN: 042-111-222-425</Text>
        </View>




        {/* Page Numbering */}
        <Text style={{ position: 'absolute', bottom: 35, right: 10, fontSize: 10, color: '#333', padding: 3 }} render={({ pageNumber, totalPages }) => (
          `Page ${pageNumber} of ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  );
}
const TechPDF = ({ data, logoBase64 }) => (
  <Document>
    <Page size="A4" style={{ padding: 20, backgroundColor: '#0f1419', color: '#fff' }}>
      <View style={{ borderWidth: 2, borderColor: '#00d4ff', padding: 20 }}>
        <Text style={{ color: '#00d4ff', fontSize: 18, marginBottom: 20 }}>[ QUOTE ]</Text>
        <Text style={{ color: '#00d4ff', marginBottom: 10 }}>CLIENT_DATA:</Text>
        <Text>{data.customer_name}</Text>
        <Text>{data.address}</Text>
        <Text style={{ marginTop: 20, color: '#00d4ff' }}>QUOTE_META:</Text>
        <Text>ID: {data.invoice_number}</Text>
        <Text>DATE: {convertDateFormat(data.issue_date)}</Text>

        {data.items.map((item, index) => (
          <View key={index} style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 }}>
            <Text>{item.item_name}</Text>
            <Text>{data.currency}{item.price}</Text>
          </View>
        ))}

        <View style={{ marginTop: 20, alignItems: 'flex-end' }}>
          <Text style={{ color: '#00d4ff', fontWeight: 'bold' }}>
            TOTAL_DUE: {data.currency}{calculateTotal(data).toFixed(2)}
          </Text>
        </View>
      </View>




      {/* Page Numbering */}
      <Text style={{ position: 'absolute', bottom: 15, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);

const ArtisticPDF = ({ data, logoBase64 }) => (
  <Document>
    <Page size="A4" style={{ padding: 25, backgroundColor: '#fffef7' }}>
      <View style={{ borderWidth: 3, borderColor: '#d4af37', borderRadius: 15, padding: 20 }}>
        {data.company.logo && (
          <Image src={logoBase64 || (API_URL + data.company.logo)} style={{ width: 90, height: 45, alignSelf: 'center', marginBottom: 10 }} />
        )}
        <Text style={{ fontSize: 24, color: '#8B4513', textAlign: 'center', marginBottom: 5 }}>QUOTE</Text>
        <Text style={{ fontSize: 10, color: '#8B4513', textAlign: 'center', marginBottom: 25 }}>~ {data.company.name} ~</Text>

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 25 }}>
          <View style={{ backgroundColor: '#fff', padding: 15, borderRadius: 10, width: '45%' }}>
            <Text style={{ fontWeight: 'bold', color: '#8B4513', marginBottom: 8 }}>For:</Text>
            <Text>{data.customer_name}</Text>
            <Text>{data.address}</Text>
            <Text>{data.phone}</Text>
            {data.email && <Text>{data.email}</Text>}
          </View>
          <View style={{ backgroundColor: '#fff', padding: 15, borderRadius: 10, width: '45%', textAlign: 'right' }}>
            <Text style={{ fontWeight: 'bold', color: '#8B4513' }}>QUOTE Details</Text>
            <Text>#{data.invoice_number}</Text>
            <Text>{convertDateFormat(data.issue_date)}</Text>
          </View>
        </View>

        <View style={{ backgroundColor: '#fff', padding: 20, borderRadius: 15, marginBottom: 20 }}>
          <View style={{ flexDirection: 'row', borderBottomWidth: 2, borderBottomColor: '#d4af37', paddingBottom: 10, marginBottom: 10 }}>
            <Text style={{ width: '40%', color: '#8B4513', fontWeight: 'bold' }}>Item</Text>
            <Text style={{ width: '20%', color: '#8B4513', fontWeight: 'bold', textAlign: 'center' }}>Qty</Text>
            <Text style={{ width: '20%', color: '#8B4513', fontWeight: 'bold', textAlign: 'center' }}>Rate</Text>
            <Text style={{ width: '20%', color: '#8B4513', fontWeight: 'bold', textAlign: 'center' }}>Amount</Text>
          </View>

          {data.items.map((item, index) => (
            <View key={index} style={{ flexDirection: 'row', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#ddd', borderStyle: 'dotted' }}>
              <Text style={{ width: '40%' }}>{item.item_name}</Text>
              <Text style={{ width: '20%', textAlign: 'center' }}>{item.quantity}</Text>
              <Text style={{ width: '20%', textAlign: 'center' }}>{data.currency}{item.unit_price}</Text>
              <Text style={{ width: '20%', textAlign: 'center' }}>{data.currency}{item.price}</Text>
            </View>
          ))}
        </View>

        <View style={{ alignItems: 'flex-end' }}>
          <View style={{ backgroundColor: '#d4af37', color: '#fff', padding: 15, borderRadius: 10 }}>
            <Text style={{ color: '#fff', marginBottom: 5 }}>Subtotal: {data.currency}{calculateSubtotal(data.items)}</Text>
            <Text style={{ color: '#fff', marginBottom: 5 }}>Tax: {data.currency}{calculateTax(data).toFixed(2)}</Text>
            <View style={{ borderTopWidth: 2, borderTopColor: '#fff', paddingTop: 8, marginTop: 8 }}>
              <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 14 }}>
                Total: {data.currency}{calculateTotal(data).toFixed(2)}
              </Text>
            </View>
          </View>
        </View>
      </View>




      {/* Page Numbering */}
      <Text style={{ position: 'absolute', bottom: 15, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);

const GeometricPDF = ({ data, logoBase64 }) => (
  <Document>
    <Page size="A4" style={{ backgroundColor: '#fff' }}>
      {/* Header with geometric shape */}
      <View style={{ backgroundColor: '#ff4757', padding: 20, color: '#fff', marginBottom: 20 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <View>
            {data.company.logo && (
              <Image src={logoBase64 || (API_URL + data.company.logo)} style={{ width: 80, height: 40, marginBottom: 10 }} />
            )}
            <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#fff' }}>QUOTE</Text>
          </View>
          <View style={{ textAlign: 'right' }}>
            <Text style={{ color: '#fff' }}>{data.company.name}</Text>
            <Text style={{ color: '#fff' }}>{data.company.address}</Text>
            <Text style={{ color: '#fff' }}>{data.company.email}</Text>
          </View>
        </View>
      </View>

      <View style={{ padding: 20 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 25 }}>
          <View>
            <View style={{ backgroundColor: '#2f3542', color: '#fff', padding: 8, marginBottom: 8 }}>
              <Text style={{ color: '#fff', fontWeight: 'bold' }}>BILL TO</Text>
            </View>
            <Text>{data.customer_name}</Text>
            <Text>{data.address}</Text>
            <Text>{data.phone}</Text>
            {data.email && <Text>{data.email}</Text>}
          </View>
          <View style={{ textAlign: 'right' }}>
            <View style={{ backgroundColor: '#ff6348', color: '#fff', padding: 8, marginBottom: 8 }}>
              <Text style={{ color: '#fff', fontWeight: 'bold' }}>DETAILS</Text>
            </View>
            <Text>Invoice: {data.invoice_number}</Text>
            <Text>Date: {convertDateFormat(data.issue_date)}</Text>
            <Text>Order: {data.order_number}</Text>
          </View>
        </View>

        {/* Table */}
        <View style={{ marginBottom: 20 }}>
          <View style={{ flexDirection: 'row', backgroundColor: '#2f3542', padding: 12 }}>
            <Text style={{ width: '40%', color: '#fff', fontWeight: 'bold' }}>Description</Text>
            <Text style={{ width: '20%', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Qty</Text>
            <Text style={{ width: '20%', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Price</Text>
            <Text style={{ width: '20%', color: '#fff', fontWeight: 'bold', textAlign: 'right' }}>Total</Text>
          </View>

          {data.items.map((item, index) => (
            <View key={index} style={{
              flexDirection: 'row',
              padding: 10,
              backgroundColor: index % 2 ? '#f8f9fa' : '#fff',
              borderBottomWidth: 1,
              borderBottomColor: '#ddd'
            }}>
              <Text style={{ width: '40%' }}>{item.item_name}</Text>
              <Text style={{ width: '20%', textAlign: 'center' }}>{item.quantity}</Text>
              <Text style={{ width: '20%', textAlign: 'center' }}>{data.currency}{item.unit_price}</Text>
              <Text style={{ width: '20%', textAlign: 'right' }}>{data.currency}{item.price}</Text>
            </View>
          ))}
        </View>

        <View style={{ alignItems: 'flex-end' }}>
          <View style={{ backgroundColor: '#f8f9fa', padding: 15 }}>
            <Text style={{ marginBottom: 5 }}>Subtotal: {data.currency}{calculateSubtotal(data.items)}</Text>
            <Text style={{ marginBottom: 5 }}>Tax: {data.currency}{calculateTax(data).toFixed(2)}</Text>
            <Text style={{ fontWeight: 'bold', color: '#ff4757', fontSize: 12 }}>
              Total: {data.currency}{calculateTotal(data).toFixed(2)}
            </Text>
          </View>
        </View>
      </View>




      {/* Page Numbering */}
      <Text style={{ position: 'absolute', bottom: 15, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);

const LuxuryPDF = ({ data, logoBase64 }) => (
  <Document>
    <Page size="A4" style={{ padding: 30, backgroundColor: '#1a1a1a', color: '#d4af37', borderWidth: 4, borderColor: '#d4af37' }}>
      {/* Header */}
      <View style={{ textAlign: 'center', marginBottom: 30, borderBottomWidth: 3, borderBottomColor: '#d4af37', paddingBottom: 20 }}>
        {data.company.logo && (
          <Image src={logoBase64 || (API_URL + data.company.logo)} style={{ width: 100, height: 50, marginBottom: 15, alignSelf: 'center' }} />
        )}
        <Text style={{ fontSize: 32, color: '#d4af37', fontWeight: 'bold', letterSpacing: 3 }}>QUOTE</Text>
        <Text style={{ fontSize: 10, color: '#ccc', marginTop: 5, letterSpacing: 1 }}>PREMIUM SERVICES</Text>
      </View>

      {/* Client and Invoice Info */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 30 }}>
        <View style={{ backgroundColor: '#2a2a2a', padding: 20, borderWidth: 2, borderColor: '#d4af37', width: '47%' }}>
          <Text style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 10, fontSize: 12 }}>DISTINGUISHED CLIENT</Text>
          <Text style={{ color: '#fff', fontWeight: 'bold' }}>{data.customer_name}</Text>
          <Text style={{ color: '#ccc' }}>{data.address}</Text>
          <Text style={{ color: '#ccc' }}>{data.phone}</Text>
        </View>
        <View style={{ backgroundColor: '#2a2a2a', padding: 20, borderWidth: 2, borderColor: '#d4af37', textAlign: 'right', width: '47%' }}>
          <Text style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 10, fontSize: 12 }}>INVOICE PARTICULARS</Text>
          <Text style={{ color: '#fff' }}>Invoice Number: {data.invoice_number}</Text>
          <Text style={{ color: '#ccc' }}>Issue Date: {convertDateFormat(data.issue_date)}</Text>
          <Text style={{ color: '#ccc' }}>Order Reference: {data.order_number}</Text>
        </View>
      </View>

      {/* Table */}
      <View style={{ backgroundColor: '#2a2a2a', borderWidth: 2, borderColor: '#d4af37', marginBottom: 30 }}>
        <View style={{ flexDirection: 'row', backgroundColor: '#d4af37', color: '#1a1a1a', padding: 15 }}>
          <Text style={{ width: '40%', color: '#1a1a1a', fontWeight: 'bold', fontSize: 11 }}>SERVICE DESCRIPTION</Text>
          <Text style={{ width: '20%', color: '#1a1a1a', fontWeight: 'bold', fontSize: 11, textAlign: 'center' }}>QUANTITY</Text>
          <Text style={{ width: '20%', color: '#1a1a1a', fontWeight: 'bold', fontSize: 11, textAlign: 'center' }}>UNIT RATE</Text>
          <Text style={{ width: '20%', color: '#1a1a1a', fontWeight: 'bold', fontSize: 11, textAlign: 'center' }}>LINE TOTAL</Text>
        </View>

        {data.items.map((item, index) => (
          <View key={index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#444', padding: 12 }}>
            <View style={{ width: '40%' }}>
              <Text style={{ color: '#fff', fontWeight: 'bold' }}>{item.item_name}</Text>
              <Text style={{ fontSize: 9, color: '#ccc', marginTop: 2 }}>{item.description}</Text>
            </View>
            <Text style={{ width: '20%', color: '#d4af37', textAlign: 'center' }}>{item.quantity}</Text>
            <Text style={{ width: '20%', color: '#d4af37', textAlign: 'center' }}>{data.currency}{item.unit_price}</Text>
            <Text style={{ width: '20%', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>{data.currency}{item.price}</Text>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={{ alignItems: 'flex-end' }}>
        <View style={{ backgroundColor: '#2a2a2a', borderWidth: 2, borderColor: '#d4af37', padding: 20, minWidth: 250 }}>
          <Text style={{ marginBottom: 10, color: '#ccc' }}>Subtotal: <Text style={{ color: '#fff' }}>{data.currency}{calculateSubtotal(data.items)}</Text></Text>
          {data?.discount > 0 && data?.discount_mode !== 'apply_to_items' && (
            <Text style={{ marginBottom: 10, color: '#ccc' }}>Discount ({data.discount_type === 'fixed' ? 'Fixed' : data.discount + '%'}): <Text style={{ color: '#fff' }}>-{data.currency}{getDiscountAmount(calculateSubtotal(data.items), data.discount, data.discount_type).toFixed(2)}</Text></Text>
          )}
          <Text style={{ marginBottom: 15, color: '#ccc' }}>Tax ({data.gst}%): <Text style={{ color: '#fff' }}>{data.currency}{calculateTax(data).toFixed(2)}</Text></Text>
          <View style={{ borderTopWidth: 3, borderTopColor: '#d4af37', paddingTop: 15, textAlign: 'center' }}>
            <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#d4af37', letterSpacing: 1 }}>TOTAL AMOUNT DUE</Text>
            <Text style={{ fontSize: 20, color: '#d4af37', fontWeight: 'bold' }}>{data.currency}{calculateTotal(data).toFixed(2)}</Text>
          </View>
        </View>
      </View>




      {/* Page Numbering */}
      <Text style={{ position: 'absolute', bottom: 15, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);

// ===== Template Mapping =====
const invoicePDFStyle = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    color: '#000',
    lineHeight: 1.5,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  companyInfo: {
    width: '50%',
  },
  logo: {
    width: 90,
    marginBottom: 5,
  },
  invoiceTitle: {
    fontSize: 18,
    fontWeight: 600,
    paddingBottom: 30,
    textAlign: 'center',
    marginVertical: 10,
    borderBottom: 3,
    borderBottomColor: '#c75258',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 15,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#c75258',
    color: '#fff',
    fontSize: 12,
    paddingVertical: 5,
    paddingHorizontal: 5
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: 1,
    borderColor: '#c0c0c0',
    padding: 5,
    borderStyle: 'dotted'
  },
  tableColDesc: {
    width: '70%',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColQty: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColUnitPrice: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColPrice: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
  },
  paymentSection: {
    flexDirection: 'row',
    marginTop: 15,
  },
  paymentInstructions: {
    width: '50%',
    backgroundColor: '#f2f2f2',
    borderWidth: 2,
    borderColor: '#c75258',
    textAlign: 'center',
    padding: 5,
    // paddingTop:35
  },
  totals: {
    width: '50%',
    textAlign: 'right',
  },
  totalRow: {
    padding: 5,
    fontSize: 12,
    backgroundColor: '#f3f3f3',
  },
  totalRowBold: {
    padding: 5,
    fontSize: 13,
    fontWeight: 700,
  },
  divider: {
    borderBottom: 1,
    borderColor: '#c75258',
    marginVertical: 5,
  },
  termsSection: {
    marginTop: 15,
    fontSize: 12,
    textAlign: 'center',
  },
  footerLogos: {
    flexDirection: 'row',
    justifyContent: 'center',
    columnGap: 8,
    borderTop: 1,
    borderColor: '#c0c0c0',
    paddingTop: 15,
    marginTop: 40,
  },
  footerLogo: {
    objectFit: 'contain'
  },
  descriptionParagraph: {
    marginBottom: 0,
    fontSize: 8.5,
    lineHeight: 1.3,
  },
  descriptionBold: {
    fontWeight: 700,
    fontSize: 8.5,
    lineHeight: 1.3,
  },
  listItem: {
    marginLeft: 5,
    marginBottom: 3,
    fontSize: 9
  },
})
const InvoicePDF = ({ data, showLogo = true, showAddress = true, showPhone = true, showVAT = true, showDescription = true, showTerms = true, footerContent, logoBase64 }) => {
  const calculateSubtotal = () => {
    return data?.items?.reduce((acc, item) => acc + parseFloat(item.price || 0), 0) || 0;
  };

  const calculateDiscountedTotal = () => {
    const { subtotalAfterDiscount } = calculateTaxAmounts(calculateSubtotal(), getEffectiveDiscountPercentage(calculateSubtotal(), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type);
    return subtotalAfterDiscount;
  };

  const calculateTax = () => {
    const { totalTax } = calculateTaxAmounts(calculateSubtotal(), getEffectiveDiscountPercentage(calculateSubtotal(), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type);
    return totalTax;
  };

  const calculateTotal = () => {
    const { total } = calculateTaxAmounts(calculateSubtotal(), getEffectiveDiscountPercentage(calculateSubtotal(), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type);
    return total;
  };

  const convertDateFormat = (dateString) => {
    if (dateString) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    }
    return '';
  };

  const currency = data?.currency || 'PKR';

  return (
    <Document>
      <Page size="A4" style={invoicePDFStyle.page} wrap>
        <View style={invoicePDFStyle.header} fixed>
          <View style={invoicePDFStyle.companyInfo}>
            {showLogo && (
              <Image style={invoicePDFStyle.logo} src={logoBase64 || (API_URL + data?.company?.logo)} />
            )}
            <Text style={{ fontSize: 11, fontWeight: 'bold' }}>{data?.company?.name || 'R-Wire Limited'}</Text>
            {showAddress && <Text style={{ fontSize: 10, width: 125, lineHeight: 1.3, color: '#777474' }}>{data?.company?.address}</Text>}
            {showAddress && showVAT && <Text style={{ fontSize: 10, width: 140, marginTop: 10, color: '#535151' }}>VAT reg no: {data?.company?.vat}</Text>}
            {showPhone && (
              <Text style={{ fontSize: 10, marginTop: 0, color: '#535151' }}>
                Email:
                <Text style={{ textDecoration: 'underline', fontSize: 10, marginTop: 0, color: '#535151' }}>
                  {data?.company?.email || ''}
                </Text>
              </Text>
            )}
          </View>
          <View>
            <Text style={{ fontSize: 10, color: '#535151' }}>Issue Date: {convertDateFormat(data?.issue_date)}</Text>
          </View>
        </View>
        <View style={invoicePDFStyle.invoiceTitle}>
          <Text>QUOTE</Text>
        </View>
        <View style={invoicePDFStyle.table}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View style={{ width: '50%' }}>
              <Text style={{ fontWeight: 'bold', fontSize: 11 }}>Bill To:</Text>
              <Text style={{ fontSize: 10, color: '#000' }}>{data?.customer_name || 'Akira'}</Text>
              <Text style={{ fontSize: 10, color: '#535151' }}>{data?.phone || '07805410487'}</Text>
              <Text style={{ fontSize: 10, width: 140, color: '#535151' }}>{data?.address || ''}</Text>
            </View>
            <View style={{ width: '50%', textAlign: 'right' }}>
              <Text style={{ fontSize: 10, color: '#535151' }}><Text style={{ fontWeight: 'bold' }}>Quote No</Text>: {data?.invoice_number || ''}</Text>
              {data?.order_number && <Text style={{ fontSize: 7, color: '#535151' }}><Text style={{ fontWeight: 'bold' }}>Order No</Text>: {data.order_number}</Text>}
            </View>
          </View>
        </View>
        <View style={invoicePDFStyle.table}>
          <View style={invoicePDFStyle.tableHeader}>
            <Text style={[invoicePDFStyle.tableColDesc, { fontWeight: 'bold', fontSize: 11 }]}>Description</Text>
            <Text style={[invoicePDFStyle.tableColQty, { fontWeight: 'bold', fontSize: 11 }]}>Quantity</Text>
            <Text style={[invoicePDFStyle.tableColUnitPrice, { fontWeight: 'bold', fontSize: 11 }]}>Unit Price</Text>
            <Text style={[invoicePDFStyle.tableColPrice, { fontWeight: 'bold', fontSize: 11 }]}>Cost</Text>
          </View>
          {data?.items?.filter(item => item.item_name && item.price).map((item, index) => (
            <View
              key={index}
              style={{
                ...invoicePDFStyle.tableRow,
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f3f3f3',
              }}
            >
              <View style={invoicePDFStyle.tableColDesc}>
                <Text>{item.item_name || 'N/A'}</Text>
                {showDescription && (
                  <View style={{ marginTop: 0 }}>
                    {parseHtmlToPdf(item.description || 'N/A')}
                  </View>
                )}
              </View>
              <Text style={[invoicePDFStyle.tableColQty, { paddingTop: item.description ? 20 : 0, fontSize: 9 }]}>{item.quantity || 1}</Text>
              <Text style={[invoicePDFStyle.tableColUnitPrice, { paddingTop: item.description ? 20 : 0, fontSize: 9 }]}>{currency}{parseFloat(item.unit_price || 0).toFixed(2)}</Text>
              <Text style={[invoicePDFStyle.tableColPrice, { paddingTop: item.description ? 20 : 0, fontSize: 9 }]}>{currency}{parseFloat(item.price || 0).toFixed(2)}</Text>
            </View>
          ))}
        </View>
        <View style={invoicePDFStyle.paymentSection}>
          {/* <View style={invoicePDFStyle.paymentInstructions}>
            <Text style={{ fontWeight: 'bold', fontSize: 12 }}>Payment Instructions</Text>
            <Text style={{ fontSize: 10, marginBottom: -4 }}>{data?.company?.accountTitle || 'R-Wire Limited'}</Text>
            <Text style={{ fontSize: 10, marginBottom: -4 }}>Sort code: {data?.company?.sortCode || '09-01-29'}</Text>
            <Text style={{ fontSize: 10, marginBottom: -4 }}>Account no: {data?.company?.accountNumber || '35103808'}</Text>
          </View> */}
          <View style={[invoicePDFStyle.totals, { width: '100%' }]}>
            <Text style={[invoicePDFStyle.totalRow, { fontSize: 11, fontWeight: 500 }]}>Total Net Amount: {currency}{calculateSubtotal().toFixed(2)}</Text>
            {data?.discount > 0 && data?.discount_mode !== 'apply_to_items' && (
              <Text style={[invoicePDFStyle.totalRow, { fontSize: 11, fontWeight: 500 }]}>Discount{(data?.discount_type === 'percentage' || data?.discount_type === 'percent' || data?.discount_type !== 'fixed') && data?.discount ? ` (${data.discount}%)` : ''}: {currency}{getDiscountAmount(calculateSubtotal(), data?.discount, data?.discount_type).toFixed(2)}</Text>
            )}
            {data?.tax > 0 && (
              <Text style={[invoicePDFStyle.totalRow, { fontSize: 11, fontWeight: 500 }]}>Total VAT Amount: {currency}{calculateTax().toFixed(2)}</Text>
            )}
            {Number(data?.special_less_value) > 0 && (
              <Text style={[invoicePDFStyle.totalRow, { fontSize: 11, fontWeight: 500 }]}>Special Less {data?.less_by ? `By ${data.less_by}` : ''}{(data?.special_less_type === 'percentage' || data?.special_less_type === 'percent') && data?.special_less_value ? ` (${data.special_less_value}%)` : ''}: -{currency}{(calculateTaxAmounts(calculateSubtotal(), getEffectiveDiscountPercentage(calculateSubtotal(), data?.discount, data?.discount_type), data?.gst, data?.tax, data?.special_less_value, data?.special_less_type).specialDiscountAmount).toFixed(2)}</Text>
            )}
            <View style={invoicePDFStyle.divider} />
            <Text style={[invoicePDFStyle.totalRowBold, { fontSize: 11 }]}>Invoice Total: {currency}{calculateTotal().toFixed(2)}</Text>
            <View style={invoicePDFStyle.divider} />

          </View>
        </View>

        <View style={invoicePDFStyle.termsSection}>
          {parseHtmlToPdf(footerContent || '')}
          <Text style={{ fontWeight: 'bold', fontSize: 10, marginTop: 15 }}>
            {data?.company?.name || 'Company Name'}
          </Text>
          <Text style={{ fontSize: 10 }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</Text>
          <Text style={{ fontSize: 10 }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</Text>
        </View>





        {/* Page Numbering */}
        <Text style={{ position: 'absolute', bottom: 15, right: 20, fontSize: 10, color: '#333', backgroundColor: '#fff', padding: 3 }} render={({ pageNumber, totalPages }) => (
          `Page ${pageNumber} of ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  );
};
// ========== Template 9: Coppergat Cables Style (Preview) ========== //
// ========== Template 9: Coppergat Cables Official Style ========== //
export const CoppergatPreview = ({ data, footerContent }) => {
 const signature = parseUserSignature(data.user_signature, data.creator || data.user);
  
  const terms = parseTermsConditions(data.terms_conditions);
  const isApplyToItems = (data?.discount_mode === 'apply_to_items' || data?.discountMode === 'apply_to_items') && Number(data?.discount) > 0;
  const rawSubtotal = (data?.items || []).reduce((sum, item) => sum + (parseFloat(item.price) || (parseFloat(item.quantity || item.qty || 0) * parseFloat(item.unit_price || 0)) || 0), 0);
  const effDiscountPct = getEffectiveDiscountPercentage(rawSubtotal, data?.discount, data?.discount_type);

  const displayItems = isApplyToItems
    ? (data?.items || []).map(item => {
        const qty = parseFloat(item.quantity || item.qty || 0);
        const originalUnitPrice = parseFloat(item.unit_price || 0);
        const effectiveUnitPrice = Math.round(originalUnitPrice * (1 - effDiscountPct / 100));
        const effectivePrice = Math.round(qty * effectiveUnitPrice);
        return {
          ...item,
          unit_price: effectiveUnitPrice,
          price: effectivePrice,
        };
      })
    : (data?.items || []).map(item => {
        const qty = parseFloat(item.quantity || item.qty || 0);
        const unitPrice = Math.round(parseFloat(item.unit_price || 0));
        const disc = Number(item.discount || 0);
        const discType = item.discount_type || item.discountType || 'percentage';
        return {
          ...item,
          price: calculateItemPrice(qty, unitPrice, disc, discType),
        };
      });

  const subtotal = displayItems.reduce(
    (sum, item) => sum + (parseFloat(item.price) || 0),
    0
  );

  const specialLessValue = Number(data?.special_less_value) || 0;
  const specialLessType = data?.special_less_type || 'fixed';
  const lessby = (data?.less_by) || 0;
  const { gstAmount: gst, vatAmount: tax, total: netPayable } = calculateTaxAmounts(
    subtotal,
    isApplyToItems ? 0 : effDiscountPct,
    data?.gst,
    data?.tax,
    specialLessValue,
    specialLessType
  );

  const logoUrl = data?.company?.logo
    ? (data.company.logo.startsWith('http') ? data.company.logo : API_URL + data.company.logo)
    : null;

  const thStyle = {
    background: "#222",
    color: "#fff",
    padding: "3px",
    border: "1px solid #444",
    textAlign: "center",
  };

  const tdStyle = {
    padding: "2px 4px",
    border: "1px solid #ccc",
  };

  const tdCenter = {
    ...tdStyle,
    textAlign: "center",
  };

  const tdRight = {
    ...tdStyle,
    textAlign: "right",
  };

  const totalLabel = {
    padding: "3px 10px",
    border: "1.5px solid #222",
    fontWeight: "bold",
  };

  const totalValue = {
    padding: "3px 10px",
    border: "1.5px solid #222",
    textAlign: "right",
    width: "180px",
    fontWeight: "bold",
  };

  return (
    <div
      style={{
        fontSize: "11px",
        fontFamily: "Arial, sans-serif",
        background: "#fff",
        maxWidth: "900px",
        margin: "0 auto",
        boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
      }}
    >
      <style>{`.coppergat-item-desc p { display: inline; margin: 0; }`}</style>
      {/* Top Banner */}
      {/* <div
        style={{
          background: "#c8102e",
          color: "#fff",
          textAlign: "center",
          padding: "7px 0",
          fontSize: "20px",
          fontWeight: "bold",
          letterSpacing: "30px",
        }}
      >
        COPPERGAT CABLES
      </div> */}

      {logoUrl && (
        <div style={{ position: 'relative', textAlign: 'left', paddingLeft: '20px', marginTop: '15px' }}>
          <img src={logoUrl} alt="Logo" style={{ width: '205px', height: '92px', objectFit: 'contain' }} />
          <div style={{ position: 'absolute', right: '20px', top: '10px' }}>
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=Verified%20by%20Coppergat" alt="Verified by Coppergat QR Code" style={{ width: 80, height: 80 }} />
          </div>
        </div>
      )}

      {splashImg && (
        <div style={{ pageBreakAfter: 'always', textAlign: 'center', margin: '20px 0' }}>
          <img src={splashImg} alt="Splash" style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain' }} />
        </div>
      )}

      {/* Logo */}
      {/* <div style={{ textAlign: "center", padding: "16px 0 4px" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            border: "2px solid #222",
            padding: "6px 16px",
          }}
        >
          <div
            style={{
              width: "38px",
              height: "38px",
              border: "3px solid #c8102e",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              color: "#c8102e",
              fontSize: "18px",
            }}
          >
            C
          </div>

          <div>
            <div
              style={{
                fontSize: "22px",
                fontWeight: 900,
                letterSpacing: "2px",
              }}
            >
              COPPERGAT
              <span
                style={{
                  fontSize: "9px",
                  verticalAlign: "super",
                }}
              >
                TM
              </span>
            </div>

            <div
              style={{
                fontSize: "9px",
                color: "#555",
              }}
            >
              Ø´Ø±ÙƒØ© Ø§Ù„ÙƒÙŠØ¨Ù„Ø§Øª
            </div>

            <div
              style={{
                fontSize: "9px",
                letterSpacing: "5px",
                color: "#555",
                textAlign: "right",
              }}
            >
              CABLES
            </div>
          </div>
        </div>

        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "13px",
            margin: "10px 0 14px",
          }}
        >
          QUOTATION SHEET
        </div>
      </div> */}

      {/* Customer Info */}
      <div style={{ margin: "0 20px", border: "1.5px solid #000" }}>
        <div style={{ backgroundColor: "#000", color: "#fff", textAlign: "center", fontWeight: "bold", padding: "4px", height: "15px", fontSize: "11px" }}></div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", flexDirection: "row", borderTop: "1px solid #000", minHeight: "20px" }}>
            <div style={{ width: "90px", fontWeight: "bold", padding: "2px 4px", borderRight: "1px solid #000", fontSize: "10px", display: "flex", alignItems: "center" }}>Ref No:</div>
            <div style={{ flex: 1, padding: "2px 4px", fontSize: "10px", display: "flex", alignItems: "center" }}>{data.invoice_number}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", borderTop: "1px solid #000", minHeight: "20px" }}>
            <div style={{ width: "90px", fontWeight: "bold", padding: "2px 4px", borderRight: "1px solid #000", fontSize: "10px", display: "flex", alignItems: "center" }}>Company Name:</div>
            <div style={{ flex: 1, padding: "2px 4px", fontSize: "10px", display: "flex", alignItems: "center" }}>{data.customer_name}</div>
            <div style={{ width: "60px", fontWeight: "bold", padding: "2px 4px", borderLeft: "1px solid #000", borderRight: "1px solid #000", fontSize: "10px", display: "flex", alignItems: "center" }}>Dated:</div>
            <div style={{ width: "90px", padding: "2px 4px", fontSize: "10px", display: "flex", alignItems: "center" }}>{new Date(data.issue_date).toLocaleDateString("en-GB")}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", borderTop: "1px solid #000", minHeight: "20px" }}>
            <div style={{ width: "90px", fontWeight: "bold", padding: "2px 4px", borderRight: "1px solid #000", fontSize: "10px", display: "flex", alignItems: "center" }}>Attn To:</div>
            <div style={{ flex: 1, padding: "2px 4px", fontSize: "10px", display: "flex", alignItems: "center" }}>{data?.attn_to}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", borderTop: "1px solid #000", minHeight: "20px" }}>
            <div style={{ width: "90px", fontWeight: "bold", padding: "2px 4px", borderRight: "1px solid #000", fontSize: "10px", display: "flex", alignItems: "center" }}>Address:</div>
            <div style={{ flex: 1, padding: "2px 4px", fontSize: "10px", display: "flex", alignItems: "center" }}>{data.address}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", borderTop: "1px solid #000", minHeight: "20px" }}>
            <div style={{ width: "90px", fontWeight: "bold", padding: "2px 4px", borderRight: "1px solid #000", fontSize: "10px", display: "flex", alignItems: "center" }}>Contact:</div>
            <div style={{ flex: 1, padding: "2px 4px", fontSize: "10px", display: "flex", alignItems: "center" }}>{data.phone}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", borderTop: "1px solid #000", minHeight: "20px" }}>
            <div style={{ width: "90px", fontWeight: "bold", padding: "2px 4px", borderRight: "1px solid #000", fontSize: "10px", display: "flex", alignItems: "center" }}>E-mail:</div>
            <div style={{ flex: 1, padding: "2px 4px", fontSize: "10px", display: "flex", alignItems: "center" }}>{data.email || ''}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", borderTop: "1px solid #000", minHeight: "20px" }}>
            <div style={{ width: "90px", fontWeight: "bold", padding: "2px 4px", borderRight: "1px solid #000", fontSize: "10px", display: "flex", alignItems: "center" }}>Note:</div>
            <div style={{ flex: 1, padding: "2px 4px", fontSize: "10px", fontWeight: "bold", display: "flex", alignItems: "center" }}>{data.note || ''}</div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: "8px 20px",
          fontSize: "11px",
        }}
      >
        We are pleased to submit our most competitive price of cables as
        under:
      </div>

      {/* Quotation Details */}
      {/* <div style={{ padding: "0 20px" }}>
        <div
          style={{
            border: "1.5px solid #222",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "12px",
            padding: "4px",
            background: "#f5f5f5",
          }}
        >
          QUOTATION DETAILS
        </div>
      </div> */}

      {/* Items Table */}
      <div style={{ padding: "0 20px" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "10.5px",
          }}
        >
          <thead>
            <tr>
              <th style={{ ...thStyle, width: "7%" }}>Sr No.</th>
              <th style={{ ...thStyle, textAlign: "left", width: "39%" }}>
                Description
              </th>
              <th style={{ ...thStyle, width: "8%" }}>Uom</th>
              <th style={{ ...thStyle, width: "10%" }}>Quantity</th>
              <th style={{ ...thStyle, width: "12%" }}>Rate ({data.currency})</th>
              <th style={{ ...thStyle, width: "10%" }}>Disc</th>
              <th style={{ ...thStyle, width: "14%" }}>Total</th>
            </tr>
          </thead>

          <tbody>
            {displayItems.map((item, index) => (
              <tr key={index}>
                <td style={{ ...tdCenter, width: "7%" }}>{index + 1}</td>

                <td style={{ ...tdStyle, width: "39%" }}>
                  <div>{item.item_name}</div>
                  {(item.description && item.description.replace(/<[^>]*>/g, '').trim() !== '') || (item.color && item.color !== 'null' && item.color !== 'N/A' && item.color.trim() !== '') ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
                      {item.description && item.description.replace(/<[^>]*>/g, '').trim() !== '' && (
                        <span
                          className="coppergat-item-desc"
                          style={{ fontSize: '9px', color: '#666', lineHeight: 1.3 }}
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                      {item.color && item.color !== 'null' && item.color !== 'N/A' && item.color.trim() !== '' && (
                        <span
                          style={{ fontSize: '9px', color: '#111827', lineHeight: 1.3, fontWeight: 700 }}
                        >
                          {item.color}
                        </span>
                      )}
                    </div>
                  ) : null}
                </td>

                <td style={{ ...tdCenter, width: "8%" }}>{item.uom}</td>

                <td
                  style={{
                    ...tdCenter,
                    width: "10%",
                    fontWeight: "bold",
                  }}
                >
                  {item.quantity}
                </td>

                <td style={{ ...tdRight, width: "12%" }}>
                  {parseFloat(item.unit_price || 0).toLocaleString()}
                </td>

                <td style={{ ...tdCenter, width: "10%" }}>
                  {Number(item.discount || 0) > 0 ? `${item.discount}${item.discount_type === 'fixed' ? ' PKR' : '%'}` : '-'}
                </td>

                <td style={{ ...tdRight, width: "14%" }}>
                  {parseFloat(item.price || 0).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={{ padding: "0 20px", marginTop: "10px" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <tbody>
            <tr>
              <td style={totalLabel}>Total</td>
              <td style={totalValue}>
                {subtotal.toLocaleString()}
              </td>
            </tr>
            {data?.discount > 0 && data?.discount_mode !== 'apply_to_items' && (<>
              <tr>
                <td
                  style={{
                    ...totalLabel,
                  }}
                >
                  DISCOUNT{(data?.discount_type === 'percentage' || data?.discount_type === 'percent' || data?.discount_type !== 'fixed') && data?.discount ? ` (${data.discount}%)` : ''}
                </td>

                <td
                  style={{
                    ...totalValue,
                  }}
                >
                  {Math.round(getDiscountAmount(subtotal, data?.discount, data?.discount_type)).toLocaleString()}

                </td>
              </tr>
            </>)}
            {specialLessValue > 0 && (
              <tr>
                <td style={totalLabel}>Special Less{lessby ? ` By ${lessby}` : ''}{(specialLessType === 'percentage' || specialLessType === 'percent') && specialLessValue ? ` (${specialLessValue}%)` : ''}</td>
                <td style={totalValue}>
                  {Math.round(calculateTaxAmounts(subtotal, getEffectiveDiscountPercentage(subtotal, data?.discount, data?.discount_type), data?.gst, data?.tax, specialLessValue, specialLessType).specialDiscountAmount).toLocaleString()}
                </td>
              </tr>
            )}

            {data.gst > 0 && <>
              <tr>
                <td
                  style={{
                    ...totalLabel,
                  }}
                >
                  GST {data.gst}%
                </td>

                <td
                  style={{
                    ...totalValue,
                  }}
                >
                  {Math.round(gst).toLocaleString()}
                </td>
              </tr>
            </>}
            {data.tax > 0 && <>
              <tr>
                <td
                  style={{
                    ...totalLabel,
                  }}
                >
                  Additional GST {data.tax}%
                </td>

                <td
                  style={{
                    ...totalValue,
                  }}
                >
                  {Math.round(tax).toLocaleString()}
                </td>
              </tr>
            </>}



            <tr>
              <td style={totalLabel}>Net Payable</td>
              <td style={totalValue}>
                {Math.round(netPayable).toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Badges HTML */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 30px 10px" }}>
        <img src={psqcaImg} alt="PSQCA" style={{ height: "48px", objectFit: "contain" }} />
        <img src={tuvImg} alt="TUV" style={{ height: "35px", objectFit: "contain" }} />
        <img src={sapImg} alt="SAP" style={{ height: "35px", objectFit: "contain", marginLeft: "10px", marginRight: "-6px" }} />
        <img src={kemaImg} alt="KEMA" style={{ height: "85px", objectFit: "contain" }} />
        <img src={alonImg} alt="ALON" style={{ height: "35px", objectFit: "contain" }} />
        <img src={pkstandImg} alt="Pakistan Standard" style={{ height: "48px", objectFit: "contain" }} />
        <img src={ceImg} alt="CE" style={{ height: "35px", objectFit: "contain" }} />
      </div>
      {/* Signature HTML */}
      {renderCoppergatSignatureHtml(signature)}
      {/* Terms Title */}
      {renderCoppergatTermsHtml(terms)}

      <div style={{ padding: "4px 20px 8px", fontSize: "11px", marginTop: "10px" }}>
        Looking forward to a prosperous business relationship. If you have any queries feel free to contact us.
      </div>

      {/* Badges HTML */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 30px 10px" }}>
        <img src={psqcaImg} alt="PSQCA" style={{ height: "48px", objectFit: "contain" }} />
        <img src={tuvImg} alt="TUV" style={{ height: "35px", objectFit: "contain" }} />
        <img src={sapImg} alt="SAP" style={{ height: "35px", objectFit: "contain", marginLeft: "10px", marginRight: "-6px" }} />
        <img src={kemaImg} alt="KEMA" style={{ height: "85px", objectFit: "contain" }} />
        <img src={alonImg} alt="ALON" style={{ height: "35px", objectFit: "contain" }} />
        <img src={pkstandImg} alt="Pakistan Standard" style={{ height: "48px", objectFit: "contain" }} />
        <img src={ceImg} alt="CE" style={{ height: "35px", objectFit: "contain" }} />
      </div>

      {/* Signature HTML */}
      {renderCoppergatSignatureHtml(signature)}

      {/* Footer */}
      <div
        style={{
          background: "#c8102e",
          color: "#fff",
          textAlign: "center",
          padding: "10px",
          fontSize: "12px",
          fontWeight: "bold",
          marginTop: "20px",
        }}
      >
        109-FF, Divine Mega 2, Airport Road, Lahore - Pakistan
        &nbsp;&nbsp;&nbsp;
        UAN: 042-111-222-425
      </div>
    </div>
  );
};

const thStyle = {
  background: "#222",
  color: "#fff",
  padding: "6px",
  border: "1px solid #444",
  textAlign: "center",
};

const tdStyle = {
  padding: "4px 6px",
  border: "1px solid #ccc",
};

const tdCenter = {
  ...tdStyle,
  textAlign: "center",
};

const tdRight = {
  ...tdStyle,
  textAlign: "right",
};

const totalLabel = {
  padding: "6px 12px",
  border: "1.5px solid #222",
  fontWeight: "bold",
};

const totalValue = {
  padding: "6px 12px",
  border: "1.5px solid #222",
  textAlign: "right",
  width: "180px",
  fontWeight: "bold",
};

const templateMapping = {
  1: { preview: ClassicTemplate, pdf: ClassicPDF },
  2: { preview: ModernTemplate, pdf: ModernPDF },
  3: { preview: CreativeTemplate, pdf: CreativePDF },
  4: { preview: CorporateTemplate, pdf: CorporatePDF },
  5: { preview: TechTemplate, pdf: TechPDF },
  6: { preview: ArtisticTemplate, pdf: ArtisticPDF },
  7: { preview: GeometricTemplate, pdf: GeometricPDF },
  8: { preview: renderInvoiceTemplate, pdf: InvoicePDF },
  9: { preview: CoppergatPreview, pdf: CoppergatTemplate },
};

const toBase64 = async (url) => {
  try {
    const response = await fetch(`${API_URL}/proxy-image?url=${encodeURIComponent(url)}`, {
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', url, error);
    return url;
  }
};
export default function QuotePreviewLayer({ sampleData }) {
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [showTemplateAlert, setShowTemplateAlert] = useState(false);

  const [spinner, setSpinner] = useState(false);
  const [message, setMessage] = useState({
    status: false,
    reason: "",
    desc: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [sendMethod, setSendMethod] = useState("email");
  const [sendValue, setSendValue] = useState("");
  const [showLogo, setShowLogo] = useState(true);
  const [showAddress, setShowAddress] = useState(true);
  const [showPhone, setShowPhone] = useState(true);
  const [showVAT, setShowVAT] = useState(true);
  const [showDescription, setShowDescription] = useState(true);
  const [showTerms, setShowTerms] = useState(true);
  const [logoBase64, setLogoBase64] = useState(null);
  const [invoiceFooterContent, setInvoiceFooterContent] = useState(null);
  const [footerLogosBase64, setFooterLogosBase64] = useState({
    main: null,
    child: null,
    mini: null,
    trustmark: null,
    last: null,
  });
  const [imagesLoading, setImagesLoading] = useState(true);
  const privileges = useMemo(() => {
    const storedPrivileges = localStorage.getItem("privilages");
    return storedPrivileges
      ? JSON.parse(storedPrivileges)
      : {
        invoice: {
          edit: false,
          delete: false,
          read: false,
          changeStatus: false,
        },
      };
  }, []);
  const [showPaymentLogs, setShowPaymentLogs] = useState(false);
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [invoicePaymentSummary, setInvoicePaymentSummary] = useState({
    paidBalance: 0,
    remainingBalance: 0,
  });
  const handleSendInvoiceClick = (e) => {
    e.preventDefault();
    setShowModal(true);
  };
  const fetchPaymentLogs = async () => {
    if (!sampleData?.id) return;
    setLoadingLogs(true);

    try {
      const response = await fetch(
        `${API_URL}/api/payments/invoice/${sampleData.id}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const logs = await response.json();
      if (!response.ok) throw new Error(logs.message || "Failed to fetch logs");

      // âœ… Enhance logs with user info from GoHighLevel
      const logsWithUsers = await Promise.all(
        logs.map(async (log) => {
          try {
            const userRes = await fetch(
              `https://services.leadconnectorhq.com/users/${log.user_id}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${TOKEN}`,
                  Version: "2021-07-28",
                  Accept: "application/json",
                },
              }
            );

            if (!userRes.ok) return { ...log, userName: "Unknown User" };
            const userData = await userRes.json();

            return {
              ...log,
              userName: `${userData.firstName || ""} ${userData.lastName || ""
                }`.trim(),
            };
          } catch {
            return { ...log, userName: "Unknown User" };
          }
        })
      );

      setPaymentLogs(logsWithUsers);

      const paidBalance = logsWithUsers.reduce(
        (acc, log) => acc + (parseFloat(log.received_amount) || 0),
        0
      );
      const invoiceTotal = calculateTotal();
      const remainingBalance = invoiceTotal - paidBalance;

      setInvoicePaymentSummary({
        paidBalance,
        remainingBalance,
      });
    } catch (error) {
      console.error("Error fetching payment logs:", error);
      setPaymentLogs([]);
      setInvoicePaymentSummary({
        paidBalance: 0,
        remainingBalance: calculateTotal(),
      });
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleOpenPaymentLogs = () => {
    setShowPaymentLogs(true);
    fetchPaymentLogs();
  };

  useEffect(() => {
    if (sampleData?.company?.logo) {
      toBase64(API_URL + sampleData.company.logo).then(setLogoBase64);
    }
  }, [sampleData?.company?.logo]);

  useEffect(() => {
    const fetchInvoicePrivacy = async () => {
      const api = await fetch(API_URL + "/api/user/fetch-quote/policy?locationId=" + LOCATION_ID, {
        method: "GET",
      });
      const json = await api.json();
      setInvoiceFooterContent(json?.policy?.details);
    };
    fetchInvoicePrivacy();
    fetchPaymentLogs();
  }, []);

  useEffect(() => {
    const fetchByLocation = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/templates/active/${LOCATION_ID}`
        );
        if (res.status === 404) throw new Error("Select the template");
        if (!res.ok) throw new Error("Failed to fetch template");
        const response = await res.json();
        setSelectedTemplateId(response.templateId);
        setShowTemplateAlert(false);
      } catch (err) {
        if (err.message === "Select the template") {
          setShowTemplateAlert(true);
          setSelectedTemplateId(8)
        }
        console.error(err.message);
      }
    };
    fetchByLocation();
  }, [LOCATION_ID]);

  const PreviewComponent = selectedTemplateId
    ? templateMapping[selectedTemplateId]?.preview
    : null;
  const PDFComponent = selectedTemplateId
    ? templateMapping[selectedTemplateId]?.pdf
    : null;

  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        let localUsers = [];
        try {
          const localRes = await fetch(`${API_URL}/api/user/fetch`);
          const localJson = await localRes.json();
          localUsers = localJson?.data || [];
        } catch (e) {
          console.error("Error fetching local users:", e);
        }

        let crmUsers = [];
        try {
          const token = localStorage.getItem('tokenID');
          if (token && LOCATION_ID) {
            const crmRes = await fetch(
              `https://services.leadconnectorhq.com/users/?locationId=${LOCATION_ID}`,
              {
                headers: {
                  Accept: "application/json",
                  Authorization: `Bearer ${token}`,
                  Version: "2021-07-28"
                }
              }
            );
            const crmJson = await crmRes.json();
            if (crmJson && crmJson.users) {
              crmUsers = crmJson.users;
            }
          }
        } catch (e) {
          console.error("Error fetching CRM users:", e);
        }

        const mergedMap = new Map();

        localUsers.forEach(lu => {
          mergedMap.set(lu.crm_id || String(lu.id), {
            id: lu.id,
            crm_id: lu.crm_id,
            name: lu.name,
            email: lu.email,
            phone: lu.phone,
            job_description: lu.job_description,
          });
        });

        crmUsers.forEach(cu => {
          const key = cu.id;
          const fullName = `${cu.firstName || ''} ${cu.lastName || ''}`.trim() || cu.name || '';
          const existing = mergedMap.get(key);
          if (existing) {
            existing.phone = existing.phone || cu.phone || '';
            existing.job_description = existing.job_description || cu.job_description || '';
            if (cu.email && cu.email.toLowerCase().includes('coppergat')) {
              existing.email = cu.email;
            } else {
              existing.email = existing.email || cu.email || '';
            }
            existing.name = existing.name || fullName;
          } else {
            mergedMap.set(key, {
              id: cu.id,
              crm_id: cu.id,
              name: fullName,
              email: cu.email || '',
              phone: cu.phone || '',
              job_description: cu.job_description || '',
            });
          }
        });

        setStaffList(Array.from(mergedMap.values()));
      } catch (err) {
        console.error("Error fetching staff list for quote signature:", err);
      }
    };
    fetchStaff();
  }, []);

  const matchedStaff = useMemo(() => {
    if (!staffList || !staffList.length) return null;
    const sigEmail = sampleData?.user_signature?.email || (typeof sampleData?.user_signature === 'string' ? (() => { try { return JSON.parse(sampleData.user_signature)?.email; } catch(e) { return null; } })() : null);
    const sigName = sampleData?.user_signature?.name || (typeof sampleData?.user_signature === 'string' ? (() => { try { return JSON.parse(sampleData.user_signature)?.name; } catch(e) { return null; } })() : null);
    const creatorEmail = sampleData?.creator?.email;
    const addedBy = sampleData?.added_by;

    return staffList.find(s => sigEmail && s.email && s.email.toLowerCase() === sigEmail.toLowerCase())
      || staffList.find(s => creatorEmail && s.email && s.email.toLowerCase() === creatorEmail.toLowerCase())
      || staffList.find(s => addedBy && (s.crm_id === addedBy || String(s.id) === String(addedBy)))
      || staffList.find(s => sigName && s.name && s.name.toLowerCase() === sigName.toLowerCase())
      || staffList.find(s => sampleData?.creator?.name && s.name && s.name.toLowerCase() === sampleData.creator.name.toLowerCase())
      || null;
  }, [staffList, sampleData]);

  const enrichedSampleData = useMemo(() => {
    const fallbackUser = matchedStaff || sampleData?.creator || sampleData?.user || JSON.parse(localStorage.getItem('user') || 'null');
    const parsedSignature = parseUserSignature(sampleData?.user_signature, fallbackUser);

    const isApplyToItems = (sampleData?.discount_mode === 'apply_to_items' || sampleData?.discountMode === 'apply_to_items') && Number(sampleData?.discount) > 0;
    let processedItems = sampleData?.items || [];
    let effectiveDiscount = sampleData?.discount;

    if (isApplyToItems && processedItems.length > 0) {
      const rawSubtotal = processedItems.reduce(
        (sum, item) => sum + (parseFloat(item.price) || (parseFloat(item.quantity || item.qty || 0) * parseFloat(item.unit_price || 0)) || 0),
        0
      );
      const effDiscountPct = getEffectiveDiscountPercentage(rawSubtotal, sampleData?.discount, sampleData?.discount_type);
      processedItems = processedItems.map((item) => {
        const qty = parseFloat(item.quantity || item.qty || 0);
        const originalUnitPrice = parseFloat(item.unit_price || 0);
        const effectiveUnitPrice = Math.round(originalUnitPrice * (1 - effDiscountPct / 100));
        const effectivePrice = Math.round(qty * effectiveUnitPrice);
        return {
          ...item,
          unit_price: effectiveUnitPrice,
          price: effectivePrice,
          original_unit_price: originalUnitPrice,
          original_price: parseFloat(item.price || 0),
        };
      });
      effectiveDiscount = 0;
    }

    return {
      ...sampleData,
      items: processedItems,
      discount: effectiveDiscount,
      saved_discount: sampleData?.discount,
      creator: matchedStaff || sampleData?.creator,
      user_signature: parsedSignature,
      terms_conditions: parseTermsConditions(sampleData?.terms_conditions),
    };
  }, [sampleData, matchedStaff]);

  const handleSend = async () => {
    try {
      setSpinner(true);
      let pdfBase64 = null;

      if (PDFComponent) {
        const blob = await pdf(
          <PDFComponent
            data={enrichedSampleData}
            footerContent={invoiceFooterContent}
            logoBase64={logoBase64}
          />
        ).toBlob();
        pdfBase64 = await blobToBase64(blob);
      }

      const issueDate = formatQuoteSubjectDate(enrichedSampleData.issue_date);
      const emailBody = `<div style="font-family: Arial, sans-serif; font-size: 14px;">
          <p>Dear ${enrichedSampleData.customer_name || 'Customer'},</p>
          <p>Please find attached our quotation <strong>${enrichedSampleData.invoice_number}</strong> dated ${issueDate}.</p>
          <p>Regards,<br/>Copper Gate</p>
        </div>`;

      const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
      const payload = {
        invoiceId: enrichedSampleData?.id,
        method: sendMethod,
        value: sendValue,
        pdfBase64,
        emailBody,
        currentUserEmail: currentUser?.email || '',
      };

      const response = await fetch(`${API_URL}/api/invoice/send-invoice`, {
        method: 'POST',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage({
          status: true,
          reason: 'Success',
          desc: `${sendMethod === 'email' ? 'Email' : 'SMS'} sent successfully!`,
        });
      } else {
        throw new Error(result.message || 'Failed to send');
      }
    } catch (error) {
      setMessage({
        status: false,
        reason: 'Error',
        desc: error.message,
      });
    } finally {
      setSpinner(false);
    }

    setShowModal(false);
    setSendValue('');
  };
  return (
    <div className="">
      <div className="row">
        <div className="col-md-12 text-end mb-3">
          <Link
            to="#"
            onClick={handleSendInvoiceClick}
            className="btn btn-sm btn-primary-600 radius-8 d-inline-flex align-items-center gap-1"
          >
            <Icon icon="pepicons-pencil:paper-plane" className="text-xl" />
            Send Quote
          </Link>
          {privileges?.invoice?.edit && (
            <Link
              to={`/quote-edit/${sampleData?.id}`}
              style={{ marginLeft: 10, marginRight: 10 }}
              className="btn btn-sm btn-warning radius-8 d-inline-flex align-items-center gap-1"
            >
              <Icon icon="uil:edit" className="text-xl" />
              Edit
            </Link>
          )}
          {PDFComponent && (
            <PDFDownloadLink
              document={<PDFComponent data={enrichedSampleData} footerContent={invoiceFooterContent} logoBase64={logoBase64} />}
              fileName={buildQuoteFileName(enrichedSampleData)}
              className="btn btn-sm btn-outline-primary radius-8 d-inline-flex align-items-center gap-1"

            >
              Download PDF
            </PDFDownloadLink>
          )}
        </div>

      </div>
      {showTemplateAlert && selectedTemplateId === null ? (
        <p style={{ color: "red" }}>
          Please select a template for this location
        </p>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid #dee2e6', borderRadius: '8px' }}>
          <div style={{ minWidth: '800px', backgroundColor: 'white' }}>

            {PreviewComponent && <PreviewComponent data={enrichedSampleData} footerContent={invoiceFooterContent} />}
          </div>
        </div>
      )}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <h6>Send Quote</h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="sendMethod" className="mb-3">
              <Form.Label>Send Method</Form.Label>
              <Form.Select
                value={sendMethod}
                onChange={(e) => setSendMethod(e.target.value)}
                className="radius-8"
              >
                <option value="email">Email</option>
              </Form.Select>
            </Form.Group>
            {sendMethod === 'email' ? (
              <Form.Group controlId="emailInput">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={sendValue}
                  onChange={(e) => setSendValue(e.target.value)}
                  className="radius-8"
                />
              </Form.Group>
            ) : (
              <Form.Group controlId="phoneInput">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="Enter phone number"
                  value={sendValue}
                  onChange={(e) => setSendValue(e.target.value)}
                  className="radius-8"
                />
              </Form.Group>
            )}
            {message.reason && (
              <div
                className={`mt-3 alert ${message.status ? 'alert-success' : 'alert-danger'}`}
              >
                <strong>{message.reason}:</strong> {message.desc}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSend} disabled={spinner}>
            {spinner ? 'Sending...' : 'Send'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}


